import React, { useState, useEffect, useCallback } from 'react';
import * as api from './api';
import LandingPage from './LandingPage';
import MSA2026Page from './MSA2026Page';
import {
  Logo, SearchIcon, ScaleIcon, LockIcon, LayoutDashboardIcon,
  FileTextIcon, FilePdfIcon, DownloadIcon, TrashIcon, CreditCardIcon,
  HistoryIcon, BarChartIcon, MenuIcon, XIcon, UserIcon, LogOutIcon,
  BookmarkIcon, FolderIcon, EditIcon, BookIcon,
  BellIcon, GlobeIcon, ShieldIcon, ChevronRightIcon,
} from './Icons';

// --- Styled Modal (replaces browser prompt/confirm) ---

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-800 border border-slate-700/60 rounded-lg shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-white">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1 transition-colors"><XIcon className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// --- Password Modal ---

function PasswordModal({ open, onClose, onSubmit, title, submitLabel }) {
  const [pw, setPw] = useState('');
  const [show, setShow] = useState(false);

  useEffect(() => { if (open) { setPw(''); setShow(false); } }, [open]);

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <form onSubmit={e => { e.preventDefault(); if (pw) onSubmit(pw); }}>
        <label className="block text-sm text-slate-400 mb-2">Encryption password</label>
        <div className="relative">
          <input
            type={show ? 'text' : 'password'}
            value={pw} onChange={e => setPw(e.target.value)}
            className="w-full px-4 py-2.5 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-600/40 pr-16 transition-all"
            placeholder="Enter your password"
            autoFocus
          />
          <button type="button" onClick={() => setShow(!show)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-white transition-colors">
            {show ? 'Hide' : 'Show'}
          </button>
        </div>
        <div className="flex gap-3 mt-4">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-slate-600 text-slate-300 rounded-md hover:bg-slate-700 transition-colors">Cancel</button>
          <button type="submit" disabled={!pw} className="flex-1 py-2.5 bg-amber-600 hover:bg-amber-500 disabled:bg-amber-800 text-white font-medium rounded-md transition-all active:scale-[0.98]">{submitLabel || 'Submit'}</button>
        </div>
      </form>
    </Modal>
  );
}

// --- Confirm Modal ---

function ConfirmModal({ open, onClose, onConfirm, title, message, confirmLabel, danger }) {
  return (
    <Modal open={open} onClose={onClose} title={title}>
      <p className="text-sm text-slate-400 mb-5">{message}</p>
      <div className="flex gap-3">
        <button onClick={onClose} className="flex-1 py-2.5 border border-slate-600 text-slate-300 rounded-md hover:bg-slate-700 transition-colors">Cancel</button>
        <button onClick={onConfirm}
          className={`flex-1 py-2.5 font-medium rounded-md transition-all active:scale-[0.98] ${danger ? 'bg-red-600 hover:bg-red-500 text-white' : 'bg-amber-600 hover:bg-amber-500 text-white'}`}>
          {confirmLabel || 'Confirm'}
        </button>
      </div>
    </Modal>
  );
}

// --- Loading Skeleton ---

function Skeleton({ lines = 3 }) {
  return (
    <div className="space-y-3 animate-pulse">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="bg-slate-800/60 border border-slate-700/30 rounded-lg p-4">
          <div className="h-4 bg-slate-700/60 rounded w-1/3 mb-3" />
          <div className="h-3 bg-slate-700/40 rounded w-full mb-2" />
          <div className="h-3 bg-slate-700/30 rounded w-2/3" />
        </div>
      ))}
    </div>
  );
}

// --- Word-level Diff for Side-by-Side Clause Comparison ---

function computeWordDiff(textA, textB) {
  // Simple word-level diff using LCS (longest common subsequence)
  const wordsA = textA.split(/(\s+)/);
  const wordsB = textB.split(/(\s+)/);

  // Build LCS table (optimized for reasonable lengths)
  const maxLen = 800;
  const a = wordsA.slice(0, maxLen);
  const b = wordsB.slice(0, maxLen);
  const m = a.length, n = b.length;

  // Use Hunt-Szymanski approach for large texts, simple DP for small
  if (m * n > 500000) {
    // For very large texts, fall back to sentence-level comparison
    return sentenceDiff(textA, textB);
  }

  const dp = Array.from({ length: m + 1 }, () => new Uint16Array(n + 1));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i-1] === b[j-1]) dp[i][j] = dp[i-1][j-1] + 1;
      else dp[i][j] = Math.max(dp[i-1][j], dp[i][j-1]);
    }
  }

  // Backtrack to produce diff
  const diffA = [], diffB = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && a[i-1] === b[j-1]) {
      diffA.unshift({ text: a[i-1], type: 'same' });
      diffB.unshift({ text: b[j-1], type: 'same' });
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j-1] >= dp[i-1][j])) {
      diffB.unshift({ text: b[j-1], type: 'added' });
      j--;
    } else {
      diffA.unshift({ text: a[i-1], type: 'removed' });
      i--;
    }
  }
  return { diffA, diffB };
}

function sentenceDiff(textA, textB) {
  // Sentence-level diff for very long texts
  const sentA = textA.split(/(?<=[.;])\s+/);
  const sentB = textB.split(/(?<=[.;])\s+/);
  const setB = new Set(sentB.map(s => s.trim().toLowerCase()));
  const setA = new Set(sentA.map(s => s.trim().toLowerCase()));

  const diffA = sentA.map(s => ({
    text: s + ' ',
    type: setB.has(s.trim().toLowerCase()) ? 'same' : 'removed'
  }));
  const diffB = sentB.map(s => ({
    text: s + ' ',
    type: setA.has(s.trim().toLowerCase()) ? 'same' : 'added'
  }));
  return { diffA, diffB };
}

function DiffText({ segments }) {
  return (
    <div className="text-sm leading-relaxed whitespace-pre-wrap">
      {segments.map((seg, i) => {
        if (seg.type === 'same') return <span key={i} className="text-slate-300">{seg.text}</span>;
        if (seg.type === 'removed') return <span key={i} className="bg-red-900/40 text-red-300 rounded px-0.5">{seg.text}</span>;
        if (seg.type === 'added') return <span key={i} className="bg-green-900/40 text-green-300 rounded px-0.5">{seg.text}</span>;
        return <span key={i}>{seg.text}</span>;
      })}
    </div>
  );
}

function ClauseDiffModal({ open, onClose, userClause, matchedClause, matchScore, matchCategory }) {
  const diff = open && userClause && matchedClause
    ? computeWordDiff(userClause, matchedClause)
    : null;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-800 border border-slate-700/60 rounded-lg shadow-2xl w-full max-w-6xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700/40 shrink-0">
          <div>
            <h3 className="text-base font-semibold text-white">Side-by-Side Clause Comparison</h3>
            <p className="text-xs text-slate-400 mt-1">
              Differences highlighted &middot; {matchScore ? `${Math.round(matchScore * 100)}% match` : ''} &middot;
              {matchCategory && <span className="capitalize"> {matchCategory.replace(/_/g, ' ')}</span>}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-2 transition-colors">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Legend */}
        <div className="px-5 py-2 flex items-center gap-4 border-b border-slate-700/20 shrink-0">
          <span className="flex items-center gap-1.5 text-xs">
            <span className="w-3 h-3 rounded bg-red-900/40 border border-red-800/40" />
            <span className="text-slate-400">Only in your clause</span>
          </span>
          <span className="flex items-center gap-1.5 text-xs">
            <span className="w-3 h-3 rounded bg-green-900/40 border border-green-800/40" />
            <span className="text-slate-400">Only in matched clause</span>
          </span>
          <span className="flex items-center gap-1.5 text-xs">
            <span className="w-3 h-3 rounded bg-slate-700/40" />
            <span className="text-slate-400">Common language</span>
          </span>
        </div>

        {/* Two-column diff */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 overflow-y-auto flex-1">
          <div className="p-5 border-r border-slate-700/30">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-medium px-2.5 py-1 rounded bg-amber-900/30 text-amber-300 border border-amber-800/30">Your Clause</span>
            </div>
            {diff ? <DiffText segments={diff.diffA} /> : <p className="text-sm text-slate-400">{userClause}</p>}
          </div>
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-medium px-2.5 py-1 rounded bg-blue-900/30 text-blue-300 border border-blue-800/30">Best Match (SEC Filing)</span>
            </div>
            {diff ? <DiffText segments={diff.diffB} /> : <p className="text-sm text-slate-400">{matchedClause}</p>}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-700/40 shrink-0">
          <p className="text-xs text-slate-500">Source: SEC EDGAR filing. Comparison is for reference only — not legal advice.</p>
        </div>
      </div>
    </div>
  );
}


// --- File type icon helper ---

function FileIcon({ filename, className = "w-5 h-5" }) {
  const ext = filename?.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return <FilePdfIcon className={`${className} text-red-400`} />;
  return <FileTextIcon className={`${className} text-slate-400`} />;
}

// --- Auth Form ---

function AuthForm({ onAuth, onBack }) {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotStage, setForgotStage] = useState('email'); // 'email' or 'reset'
  const [forgotMessage, setForgotMessage] = useState('');

  const strength = password.length === 0 ? null : password.length < 8 ? 'weak' : password.length < 12 ? 'medium' : 'strong';
  const strengthColor = { weak: 'bg-red-500', medium: 'bg-yellow-500', strong: 'bg-green-500' };
  const strengthWidth = { weak: 'w-1/3', medium: 'w-2/3', strong: 'w-full' };
  const passwordsMatch = !isLogin && confirmPassword.length > 0 && password !== confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isLogin && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const user = isLogin ? await api.login(email, password) : await api.register(email, password);
      onAuth(user);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotMessage('');
    setForgotLoading(true);
    try {
      const response = await fetch('/api/auth/forgot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      if (!response.ok) throw new Error('Email not found');
      setForgotMessage('Check your email for the password reset link');
      setForgotStage('reset');
    } catch (err) {
      setForgotMessage(err.message);
    }
    setForgotLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setForgotMessage('');
    if (newPassword.length < 8) {
      setForgotMessage('Password must be at least 8 characters');
      return;
    }
    setForgotLoading(true);
    try {
      const response = await fetch('/api/auth/reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, password: newPassword }),
      });
      if (!response.ok) throw new Error('Invalid or expired token');
      setForgotMessage('Password reset successful. You can now sign in.');
      setTimeout(() => {
        setShowForgotPassword(false);
        setForgotStage('email');
        setForgotEmail('');
        setResetToken('');
        setNewPassword('');
      }, 2000);
    } catch (err) {
      setForgotMessage(err.message);
    }
    setForgotLoading(false);
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotStage('email');
    setForgotEmail('');
    setResetToken('');
    setNewPassword('');
    setForgotMessage('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Logo size="large" />
          <p className="text-slate-400 mt-3 text-sm">Encrypted Legal Contract Intelligence</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-slate-800/80 rounded-lg p-8 shadow-2xl border border-slate-700/50">
          <h2 className="text-lg font-semibold text-white mb-6">{isLogin ? 'Sign In' : 'Create Account'}</h2>
          {error && (
            <div className="bg-red-900/40 border border-red-800/50 text-red-200 px-4 py-2 rounded-md mb-4 text-sm">
              {error}
              {error.includes('already registered') && (
                <button type="button" onClick={() => { setIsLogin(true); setError(''); }} className="ml-2 text-amber-400 underline">Sign in instead?</button>
              )}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                className="w-full px-4 py-2.5 bg-slate-700/80 border border-slate-600/60 rounded-md text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-600/40 transition-all"
                placeholder="you@gmail.com" />
            </div>
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="block text-sm font-medium text-slate-300">Password</label>
                {isLogin && (
                  <button type="button" onClick={() => setShowForgotPassword(true)} className="text-xs text-amber-400 hover:text-amber-300 underline">
                    Forgot password?
                  </button>
                )}
              </div>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8}
                className="w-full px-4 py-2.5 bg-slate-700/80 border border-slate-600/60 rounded-md text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-600/40 transition-all"
                placeholder="Min. 8 characters" />
              {!isLogin && strength && (
                <div className="mt-2">
                  <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
                    <div className={`h-full ${strengthColor[strength]} ${strengthWidth[strength]} transition-all duration-300 rounded-full`} />
                  </div>
                  <p className={`text-xs mt-1 ${strength === 'weak' ? 'text-red-400' : strength === 'medium' ? 'text-yellow-400' : 'text-green-400'}`}>
                    Password strength: {strength}
                  </p>
                </div>
              )}
            </div>
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Confirm Password</label>
                <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required={!isLogin} minLength={8}
                  className={`w-full px-4 py-2.5 bg-slate-700/80 border rounded-md text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-600/40 transition-all ${passwordsMatch ? 'border-red-500' : 'border-slate-600/60'}`}
                  placeholder="Re-enter your password" />
                {passwordsMatch && (
                  <p className="text-xs mt-1 text-red-400">Passwords do not match</p>
                )}
              </div>
            )}
          </div>
          {!isLogin && (
            <p className="mt-4 text-xs text-slate-500 leading-relaxed">
              By creating an account, you acknowledge that icontracts.net is a research and reference tool only. All search results, benchmarking grades, and citations are provided for informational purposes and do not constitute legal advice. You agree to exercise your own independent professional judgment in all matters. You accept full responsibility for how you use any information obtained through this platform and agree to hold icontracts.net and its affiliates harmless from any claims, losses, or liabilities arising from your use of the service. See our <a href="/terms" className="text-amber-500 hover:text-amber-400">Terms of Service</a> for full details.
            </p>
          )}
          <button type="submit" disabled={loading}
            className="w-full mt-4 py-2.5 bg-amber-600 hover:bg-amber-500 disabled:bg-amber-800 text-white font-medium rounded-md transition-all active:scale-[0.98]">
            {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
          </button>
          <p className="text-center mt-4 text-sm text-slate-400">
            {isLogin ? "Don't have an account?" : "Already have an account?"}{' '}
            <button type="button" onClick={() => { setIsLogin(!isLogin); setError(''); }}
              className="text-amber-400 hover:text-amber-300 underline">
              {isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </p>
        </form>
        <div className="text-center mt-6">
          <button onClick={onBack} className="text-sm font-medium shimmer-gold transition-colors">&larr; Back to home</button>
        </div>
        <p className="text-center mt-4 text-xs text-slate-600">
          Your files are encrypted with AES-256-GCM. We cannot access your stored documents.
        </p>
      </div>

      {/* Forgot Password Modal */}
      <Modal open={showForgotPassword} onClose={closeForgotPassword} title={forgotStage === 'email' ? 'Reset Password' : 'Set New Password'}>
        {forgotStage === 'email' ? (
          <form onSubmit={handleForgotPassword}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Email address</label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-600/40 transition-all"
                  placeholder="you@gmail.com"
                />
              </div>
              {forgotMessage && (
                <p className={`text-sm ${forgotMessage.includes('success') || forgotMessage.includes('Check') ? 'text-green-400' : 'text-red-400'}`}>
                  {forgotMessage}
                </p>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button type="button" onClick={closeForgotPassword} className="flex-1 py-2.5 border border-slate-600 text-slate-300 rounded-md hover:bg-slate-700 transition-colors">Cancel</button>
              <button type="submit" disabled={forgotLoading || !forgotEmail} className="flex-1 py-2.5 bg-amber-600 hover:bg-amber-500 disabled:bg-amber-800 text-white font-medium rounded-md transition-all active:scale-[0.98]">
                {forgotLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleResetPassword}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Reset token (from email)</label>
                <input
                  type="text"
                  value={resetToken}
                  onChange={e => setResetToken(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-600/40 transition-all text-xs"
                  placeholder="Paste the token from your reset email"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">New password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full px-4 py-2.5 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-600/40 transition-all"
                  placeholder="Min. 8 characters"
                />
              </div>
              {forgotMessage && (
                <p className={`text-sm ${forgotMessage.includes('success') ? 'text-green-400' : 'text-red-400'}`}>
                  {forgotMessage}
                </p>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button type="button" onClick={() => { setForgotStage('email'); setResetToken(''); setNewPassword(''); setForgotMessage(''); }} className="flex-1 py-2.5 border border-slate-600 text-slate-300 rounded-md hover:bg-slate-700 transition-colors">Back</button>
              <button type="submit" disabled={forgotLoading || !resetToken || newPassword.length < 8} className="flex-1 py-2.5 bg-amber-600 hover:bg-amber-500 disabled:bg-amber-800 text-white font-medium rounded-md transition-all active:scale-[0.98]">
                {forgotLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}

// --- Search Tab ---

function SearchTab({ initialQuery, onConsumeQuery }) {
  const [query, setQuery] = useState(initialQuery || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [jurisdiction, setJurisdiction] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [savedIds, setSavedIds] = useState(new Set());
  const [saveMsg, setSaveMsg] = useState('');
  const [diffModal, setDiffModal] = useState(null);

  // Extract unique categories from results for the filter bar
  const categoriesFound = results?.results
    ? [...new Set(results.results.map(r => r.category).filter(Boolean))]
    : [];

  // Filter results by selected category
  const filteredResults = categoryFilter
    ? results?.results?.filter(r => r.category === categoryFilter)
    : results?.results;

  useEffect(() => {
    if (initialQuery) {
      setQuery(initialQuery);
      onConsumeQuery();
    }
  }, [initialQuery, onConsumeQuery]);

  const suggestions = [
    'limitation of liability excluding indirect damages',
    'force majeure pandemic exclusion',
    'non-compete executive employment',
    'change of control acceleration',
    'SIAC arbitration Singapore China',
  ];

  const handleSearch = async (q) => {
    const searchQuery = q || query;
    if (!searchQuery.trim()) return;
    setQuery(searchQuery);
    setLoading(true);
    setError('');
    setCategoryFilter(null);
    try {
      const data = await api.searchClauses(searchQuery, 10, jurisdiction === 'all' ? null : jurisdiction);
      setResults(data);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleSaveClause = async (clause) => {
    try {
      await api.saveClause({
        clause_id: clause.clause_id,
        category: clause.category,
        preview: clause.preview,
        full_text: clause.full_text || '',
        score: clause.score,
        source: 'search',
      });
      setSavedIds(prev => new Set(prev).add(clause.clause_id));
      setSaveMsg('Saved to Library');
      setTimeout(() => setSaveMsg(''), 2000);
    } catch (err) {
      if (err.message.includes('already')) {
        setSaveMsg('Already in Library');
      } else {
        setSaveMsg('Save failed');
      }
      setTimeout(() => setSaveMsg(''), 2000);
    }
  };

  return (
    <div>
      {saveMsg && (
        <div className="fixed top-4 right-4 z-50 bg-amber-600 text-white text-sm px-4 py-2 rounded-lg shadow-lg animate-pulse">
          {saveMsg}
        </div>
      )}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <SearchIcon className="w-5 h-5 text-slate-400" /> Clause Discovery
        </h2>
        <p className="text-slate-400 text-sm mt-1">Search real clauses from {results?.total_indexed?.toLocaleString() || '338,000+'} filed commercial contracts</p>
      </div>
      <form onSubmit={e => { e.preventDefault(); handleSearch(); }} className="space-y-3 mb-4">
        <div className="flex gap-3 items-end">
          <div className="flex-1 relative">
            <textarea value={query} onChange={e => setQuery(e.target.value)}
              rows={query.length > 200 ? 4 : query.length > 80 ? 2 : 1}
              className="w-full px-4 py-2.5 bg-slate-800/80 border border-slate-700/60 rounded-md text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-600/40 transition-all resize-none text-sm leading-relaxed"
              placeholder='Search by keyword, paste a clause, or paste an entire contract section...' />
            {query.split(/\s+/).filter(Boolean).length > 2500 && (
              <p className="text-xs text-amber-400 mt-1">Text will be analyzed in chunks. For best results, keep within 3,000 words.</p>
            )}
          </div>
          <button type="submit" disabled={loading}
            className="px-6 py-2.5 bg-amber-600 hover:bg-amber-500 disabled:bg-amber-800 text-white font-medium rounded-md transition-all active:scale-[0.97] whitespace-nowrap shrink-0">
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Jurisdiction:</span>
          {[
            { id: 'all', label: 'All', count: '338K' },
            { id: 'crossborder', label: 'Cross-Border', count: '23.5K' },
            { id: 'china', label: 'China', count: '4.5K' },
            { id: 'singapore', label: 'Singapore', count: '1.2K' },
          ].map(j => (
            <button key={j.id} type="button" onClick={() => setJurisdiction(j.id)}
              className={`text-xs px-3 py-1.5 rounded-md transition-all ${
                jurisdiction === j.id
                  ? 'bg-amber-600 text-white'
                  : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60 border border-slate-700/40'
              }`}>
              {j.label} <span className="opacity-60 tabular-nums">({j.count})</span>
            </button>
          ))}
        </div>
      </form>

      {!results && !loading && (
        <div className="mb-6">
          <p className="text-xs text-slate-500 mb-2">Try searching:</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map(s => (
              <button key={s} onClick={() => handleSearch(s)}
                className="text-xs px-3 py-1.5 bg-slate-800/40 border border-slate-700/40 rounded-md text-slate-400 hover:text-white hover:border-slate-600/60 transition-all">
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg mb-4">{error}</div>}
      {loading && <Skeleton lines={3} />}
      {results && !loading && (
        <div className="space-y-3">
          {results.remaining_searches >= 0 && (
            <p className="text-sm text-slate-500">{results.remaining_searches} searches remaining this month</p>
          )}

          {/* Categories found summary */}
          {categoriesFound.length > 0 && (
            <div className="bg-slate-800/40 border border-slate-700/30 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-slate-500">Results across {categoriesFound.length} categor{categoriesFound.length === 1 ? 'y' : 'ies'}:</span>
                {results.chunks_analyzed > 1 && (
                  <span className="text-xs text-slate-600">({results.chunks_analyzed} text segments analyzed)</span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => setCategoryFilter(null)}
                  className={`text-xs px-2.5 py-1 rounded-md transition-all ${
                    !categoryFilter ? 'bg-amber-600 text-white' : 'bg-slate-700/60 text-slate-400 hover:text-white hover:bg-slate-700'
                  }`}
                >
                  All ({results.results?.length})
                </button>
                {categoriesFound.map(cat => {
                  const count = results.results.filter(r => r.category === cat).length;
                  return (
                    <button key={cat} onClick={() => setCategoryFilter(categoryFilter === cat ? null : cat)}
                      className={`text-xs px-2.5 py-1 rounded-md transition-all capitalize ${
                        categoryFilter === cat ? 'bg-amber-600 text-white' : 'bg-slate-700/60 text-slate-400 hover:text-white hover:bg-slate-700'
                      }`}
                    >
                      {cat.replace(/_/g, ' ')} ({count})
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {filteredResults?.length === 0 && <p className="text-slate-400">No matching clauses found.</p>}
          {filteredResults?.map((r, i) => (
            <div key={r.clause_id || i} className="bg-slate-800/60 border border-slate-700/40 rounded-lg overflow-hidden hover:border-slate-600/50 transition-all">
              <button onClick={() => setExpandedId(expandedId === r.clause_id ? null : r.clause_id)}
                className="w-full text-left p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-700/80 text-slate-300">
                      {r.category?.replace(/_/g, ' ')}
                    </span>
                    <span className="text-xs text-slate-500">SEC Filing</span>
                    {r.jurisdictions?.includes('china') && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-red-900/30 text-red-300">CN</span>
                    )}
                    {r.jurisdictions?.includes('singapore') && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-blue-900/30 text-blue-300">SG</span>
                    )}
                    {r.jurisdictions?.includes('crossborder') && !r.jurisdictions?.includes('china') && !r.jurisdictions?.includes('singapore') && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-purple-900/30 text-purple-300">XB</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500 rounded-full" style={{ width: `${Math.min(r.score * 100, 100)}%` }} />
                    </div>
                    <span className="text-xs text-slate-500 tabular-nums w-10 text-right">{Math.round(r.score * 100)}%</span>
                  </div>
                </div>
                <p className="text-sm text-slate-300 leading-relaxed line-clamp-2">{r.preview}</p>
              </button>
              {expandedId === r.clause_id && (
                <div className="border-t border-slate-700 p-5 bg-slate-850">
                  <pre className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto">{r.full_text}</pre>
                  <div className="mt-4 pt-3 border-t border-slate-700 flex items-center justify-between">
                    <p className="text-xs text-slate-500">Source: SEC EDGAR filing. For reference only — not legal advice.</p>
                    <div className="flex items-center gap-3">
                      {query.split(/\s+/).length > 30 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setDiffModal({ userClause: query, matchedClause: r.full_text, score: r.score, category: r.category }); }}
                          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-blue-900/30 text-blue-300 hover:bg-blue-900/50 border border-blue-800/30 transition-all"
                        >
                          Compare
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSaveClause(r); }}
                        className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md transition-all ${
                          savedIds.has(r.clause_id)
                            ? 'bg-amber-900/40 text-amber-400 border border-amber-700/50'
                            : 'bg-slate-700/60 text-slate-400 hover:text-white hover:bg-slate-700 border border-slate-600/40'
                        }`}
                      >
                        <BookmarkIcon className="w-3.5 h-3.5" filled={savedIds.has(r.clause_id)} />
                        {savedIds.has(r.clause_id) ? 'Saved' : 'Save to Library'}
                      </button>
                      <span className="text-xs text-slate-600">{r.clause_id}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ClauseDiffModal
        open={!!diffModal}
        onClose={() => setDiffModal(null)}
        userClause={diffModal?.userClause || ''}
        matchedClause={diffModal?.matchedClause || ''}
        matchScore={diffModal?.score}
        matchCategory={diffModal?.category}
      />
    </div>
  );
}

// --- Benchmark Tab ---

function BenchmarkTab() {
  const [mode, setMode] = useState('clause'); // 'clause' or 'scorecard'
  const [text, setText] = useState('');
  const [result, setResult] = useState(null);
  const [scorecardResult, setScorecardResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedClauseIdx, setExpandedClauseIdx] = useState(null);
  const [diffModal, setDiffModal] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [expandedSimilarId, setExpandedSimilarId] = useState(null);

  const handleBenchmark = async (e) => {
    e.preventDefault();
    if (text.trim().length < 20) return;
    setLoading(true);
    setError('');
    try {
      const data = await api.benchmarkClause(text);
      setResult(data);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleScorecard = async (e) => {
    e.preventDefault();
    if (text.trim().length < 100) return;
    setLoading(true);
    setError('');
    try {
      const data = await api.contractScorecard(text);
      setScorecardResult(data);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleDownloadReport = async (type) => {
    setDownloading(true);
    try {
      const blob = type === 'scorecard'
        ? await api.downloadScorecardReport(text)
        : await api.downloadBenchmarkReport(text);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = type === 'scorecard'
        ? `icontracts_scorecard_${new Date().toISOString().slice(0,10)}.pdf`
        : `icontracts_benchmark_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
    setDownloading(false);
  };

  const gradeConfig = {
    A: { color: 'text-green-400 bg-green-900/50 border-green-700', label: 'Strong', textColor: 'text-green-400' },
    B: { color: 'text-amber-400 bg-amber-900/50 border-amber-700', label: 'Good', textColor: 'text-amber-400' },
    C: { color: 'text-yellow-400 bg-yellow-900/50 border-yellow-700', label: 'Moderate', textColor: 'text-yellow-400' },
    D: { color: 'text-red-400 bg-red-900/50 border-red-700', label: 'Unusual', textColor: 'text-red-400' },
  };

  const wordCount = text.split(/\s+/).filter(Boolean).length;
  const wordLimit = mode === 'scorecard' ? 5000 : 3000;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <ScaleIcon className="w-5 h-5 text-slate-400" /> Clause Benchmarking
        </h2>
        <p className="text-slate-400 text-sm mt-1">Paste your clause or full contract to benchmark against real commercial filings</p>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-1 mb-4 bg-slate-800/40 rounded-lg p-1 w-fit">
        <button onClick={() => { setMode('clause'); setResult(null); setScorecardResult(null); }}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
            mode === 'clause' ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white'
          }`}>
          Single Clause
        </button>
        <button onClick={() => { setMode('scorecard'); setResult(null); setScorecardResult(null); }}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
            mode === 'scorecard' ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white'
          }`}>
          Full Contract Scorecard
        </button>
      </div>

      <form onSubmit={mode === 'clause' ? handleBenchmark : handleScorecard}>
        <textarea value={text} onChange={e => setText(e.target.value)}
          rows={mode === 'scorecard' ? 14 : 10}
          className="w-full px-4 py-3 bg-slate-800/80 border border-slate-700/60 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-600/40 text-sm leading-relaxed transition-all"
          placeholder={mode === 'scorecard'
            ? "Paste your full contract or multiple contract sections here. We will identify each clause, grade it individually, and provide a comprehensive scorecard with specific strengths and weaknesses..."
            : "Paste a clause, a full section, or an entire contract here. We analyze every paragraph — up to 3,000 words — against 338,000+ real clauses..."
          } />
        {text.trim() && (
          <div className="flex justify-between mt-1.5">
            <p className="text-xs text-slate-600">{wordCount.toLocaleString()} words</p>
            {wordCount > wordLimit && (
              <p className="text-xs text-amber-400">Text beyond {wordLimit.toLocaleString()} words will be truncated.</p>
            )}
          </div>
        )}
        <button type="submit" disabled={loading || text.trim().length < (mode === 'scorecard' ? 100 : 20)}
          className="mt-3 px-6 py-2.5 bg-amber-600 hover:bg-amber-500 disabled:bg-amber-800 text-white font-medium rounded-md transition-all active:scale-[0.97]">
          {loading ? 'Analyzing...' : mode === 'scorecard' ? 'Generate Scorecard' : 'Benchmark This Clause'}
        </button>
      </form>

      {error && <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg mt-4">{error}</div>}
      {loading && (
        <div className="mt-6">
          <Skeleton lines={mode === 'scorecard' ? 5 : 2} />
          {mode === 'scorecard' && <p className="text-xs text-slate-500 mt-3 animate-pulse">Analyzing each clause against 338,000+ filings — this may take a moment...</p>}
        </div>
      )}

      {/* Single clause result (existing) */}
      {result && !loading && mode === 'clause' && (
        <div className="mt-6 space-y-4">
          <div className={`flex items-start gap-4 px-5 py-4 rounded-lg border ${gradeConfig[result.grade]?.color || 'text-slate-400 bg-slate-800 border-slate-700'}`}>
            <span className="text-5xl font-bold tabular-nums shrink-0">{result.grade}</span>
            <div>
              <p className="text-sm font-medium">{gradeConfig[result.grade]?.label}</p>
              <p className="text-xs opacity-80 mt-0.5">{result.assessment}</p>
              {result.percentile > 0 && (
                <p className="text-xs opacity-70 mt-1">Similarity: top {result.percentile}% match against {result.corpus_size?.toLocaleString()} real commercial filings</p>
              )}
            </div>
          </div>

          {/* WHY panel */}
          {result.why && (result.why.strengths?.length > 0 || result.why.weaknesses?.length > 0 || result.why.observations?.length > 0) && (
            <div className="bg-slate-800/40 border border-slate-700/30 rounded-lg p-4 space-y-3">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Why this grade</p>
              {result.why.strengths?.length > 0 && (
                <div>
                  <p className="text-xs text-green-400 font-medium mb-1">Strengths</p>
                  <ul className="space-y-1">
                    {result.why.strengths.map((s, i) => <li key={i} className="text-xs text-slate-300 flex gap-2"><span className="text-green-500 shrink-0">+</span>{s}</li>)}
                  </ul>
                </div>
              )}
              {result.why.weaknesses?.length > 0 && (
                <div>
                  <p className="text-xs text-red-400 font-medium mb-1">Gaps vs. market standard</p>
                  <ul className="space-y-1">
                    {result.why.weaknesses.map((w, i) => <li key={i} className="text-xs text-slate-300 flex gap-2"><span className="text-red-400 shrink-0">−</span>{w}</li>)}
                  </ul>
                </div>
              )}
              {result.why.observations?.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 font-medium mb-1">Observations</p>
                  <ul className="space-y-1">
                    {result.why.observations.map((o, i) => <li key={i} className="text-xs text-slate-400 flex gap-2"><span className="text-slate-600 shrink-0">·</span>{o}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-slate-800/60 border border-slate-700/40 rounded-lg p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Detected Type</p>
              <p className="text-lg font-medium text-white mt-1 capitalize">{result.detected_type?.replace(/_/g, ' ')}</p>
            </div>
            <div className="bg-slate-800/60 border border-slate-700/40 rounded-lg p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Match Strength</p>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-lg font-medium text-white">{Math.round(result.best_similarity * 100)}%</p>
                <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-500 rounded-full" style={{ width: `${Math.min(result.best_similarity * 100, 100)}%` }} />
                </div>
              </div>
            </div>
            <div className="bg-slate-800/60 border border-slate-700/40 rounded-lg p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Corpus Size</p>
              <p className="text-lg font-medium text-white mt-1">{result.corpus_size?.toLocaleString()} clauses</p>
            </div>
          </div>
          {(result.chunks_analyzed > 1 || result.similar_clauses?.length > 0) && (
            <div className="bg-slate-800/40 border border-slate-700/30 rounded-lg p-3">
              <div className="flex flex-wrap items-center gap-2">
                {result.chunks_analyzed > 1 && (
                  <span className="text-xs text-slate-500">{result.chunks_analyzed} text segments analyzed</span>
                )}
                {result.similar_clauses?.length > 0 && (
                  <>
                    <span className="text-xs text-slate-600">|</span>
                    <span className="text-xs text-slate-500">Matched categories:</span>
                    {[...new Set(result.similar_clauses.map(c => c.category).filter(Boolean))].map(cat => (
                      <span key={cat} className="text-xs px-2 py-0.5 rounded bg-slate-700/60 text-slate-400 capitalize">{cat.replace(/_/g, ' ')}</span>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}
          {result.similar_clauses?.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-slate-300 mb-3">Most Similar Clauses</h3>
              <div className="space-y-2">
                {result.similar_clauses.map((c, i) => (
                  <div key={c.clause_id || i} className="bg-slate-800/60 border border-slate-700/40 rounded-lg overflow-hidden hover:border-slate-600/50 transition-all">
                    <button onClick={() => setExpandedSimilarId(expandedSimilarId === (c.clause_id || i) ? null : (c.clause_id || i))}
                      className="w-full text-left p-4 cursor-pointer">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700 text-slate-300 capitalize">{c.category?.replace(/_/g, ' ')}</span>
                        <span className="text-xs text-slate-600">{c.source || 'SEC EDGAR Filing (US)'}</span>
                        <div className="flex items-center gap-1.5 ml-auto">
                          <button
                            onClick={(e) => { e.stopPropagation(); setDiffModal({ userClause: text, matchedClause: c.full_text || c.preview, score: c.score, category: c.category }); }}
                            className="text-xs px-2.5 py-1 rounded-md bg-blue-900/30 text-blue-300 hover:bg-blue-900/50 border border-blue-800/30 transition-all"
                          >
                            Compare
                          </button>
                          <div className="w-12 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                            <div className="h-full bg-amber-500/70 rounded-full" style={{ width: `${Math.min(c.score * 100, 100)}%` }} />
                          </div>
                          <span className="text-xs text-slate-500">{Math.round(c.score * 100)}%</span>
                        </div>
                      </div>
                      <p className={`text-sm text-slate-400 leading-relaxed ${expandedSimilarId === (c.clause_id || i) ? '' : 'line-clamp-3'}`}>{c.preview}</p>
                    </button>
                    {expandedSimilarId === (c.clause_id || i) && c.full_text && (
                      <div className="border-t border-slate-700 p-4 bg-slate-900/40">
                        <pre className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto">{c.full_text}</pre>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">
                Benchmarked against {result.corpus_size?.toLocaleString()} clauses from SEC EDGAR, SGX, and HKEX filings. Grade reflects pattern similarity, not legal quality. Not legal advice.
              </p>
              {result.remaining_benchmarks >= 0 && (
                <p className="text-xs text-amber-500/80 mt-1">{result.remaining_benchmarks} free benchmark{result.remaining_benchmarks !== 1 ? 's' : ''} remaining this month — upgrade to Pro for unlimited</p>
              )}
            </div>
            <button
              onClick={() => handleDownloadReport('benchmark')}
              disabled={downloading}
              className="flex items-center gap-1.5 px-4 py-2 bg-slate-700/60 hover:bg-slate-700 text-white text-sm font-medium rounded-md transition-all border border-slate-600/40 disabled:opacity-50 shrink-0 ml-4"
            >
              <DownloadIcon className="w-4 h-4" />
              {downloading ? 'Generating...' : 'Download Report'}
            </button>
          </div>
        </div>
      )}

      {/* Full Contract Scorecard result */}
      {scorecardResult && !loading && mode === 'scorecard' && (
        <div className="mt-6 space-y-6">
          {/* Overall summary */}
          <div className="bg-slate-800/60 border border-slate-700/40 rounded-lg p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-20 h-20 rounded-lg border-2 flex items-center justify-center ${gradeConfig[scorecardResult.overall_grade]?.color || 'border-slate-700 bg-slate-800'}`}>
                  <span className="text-4xl font-bold">{scorecardResult.overall_grade}</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Contract Scorecard</h3>
                  <p className="text-sm text-slate-400">{scorecardResult.total_clauses_analyzed} clauses analyzed against {scorecardResult.corpus_size?.toLocaleString()} filings</p>
                  <p className="text-xs text-slate-500 mt-1">Overall GPA: {scorecardResult.overall_gpa}/4.00</p>
                </div>
              </div>
              {/* Grade distribution */}
              <div className="flex gap-3">
                {Object.entries(scorecardResult.grade_distribution || {}).map(([grade, count]) => (
                  <div key={grade} className="text-center">
                    <p className={`text-2xl font-bold tabular-nums ${gradeConfig[grade]?.textColor || 'text-slate-400'}`}>{count}</p>
                    <p className="text-xs text-slate-500">{grade}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Weakest clauses alert */}
            {scorecardResult.weakest_clauses?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-700/40">
                <p className="text-xs text-red-400 font-medium mb-1">Attention needed:</p>
                <div className="flex flex-wrap gap-2">
                  {scorecardResult.weakest_clauses.map((section, i) => (
                    <span key={i} className="text-xs px-2.5 py-1 rounded bg-red-900/30 text-red-300 border border-red-800/30">
                      {section}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Clause-by-clause breakdown */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-3">Clause-by-Clause Analysis</h3>
            <div className="space-y-2">
              {scorecardResult.clauses?.map((clause, i) => {
                const gc = gradeConfig[clause.grade] || { color: 'text-slate-400 bg-slate-800 border-slate-700', textColor: 'text-slate-400' };
                const isExpanded = expandedClauseIdx === i;

                return (
                  <div key={i} className={`bg-slate-800/60 border rounded-lg overflow-hidden transition-all ${
                    clause.grade === 'D' ? 'border-red-800/40' : clause.grade === 'C' ? 'border-yellow-800/30' : 'border-slate-700/40'
                  }`}>
                    {/* Clause header row */}
                    <button onClick={() => setExpandedClauseIdx(isExpanded ? null : i)}
                      className="w-full text-left p-4 hover:bg-slate-800/80 transition-colors">
                      <div className="flex items-center gap-3">
                        <span className={`text-lg font-bold tabular-nums w-8 ${gc.textColor}`}>{clause.grade}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-sm text-white font-medium truncate">{clause.section}</span>
                            <span className="text-xs px-2 py-0.5 rounded bg-slate-700/80 text-slate-300 capitalize shrink-0">
                              {clause.detected_category?.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 line-clamp-1">{clause.text_preview}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                            <div className="h-full bg-amber-500 rounded-full" style={{ width: `${Math.min(clause.similarity * 100, 100)}%` }} />
                          </div>
                          <span className="text-xs text-slate-500 tabular-nums w-10 text-right">{Math.round(clause.similarity * 100)}%</span>
                          <svg className={`w-4 h-4 text-slate-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6" /></svg>
                        </div>
                      </div>
                    </button>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div className="border-t border-slate-700 p-5 space-y-4">
                        {/* Strengths */}
                        {clause.assessment?.strengths?.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-green-400 mb-2">Strengths</p>
                            <ul className="space-y-1">
                              {clause.assessment.strengths.map((s, j) => (
                                <li key={j} className="text-sm text-slate-300 flex items-start gap-2">
                                  <span className="text-green-500 mt-0.5 shrink-0">+</span>
                                  <span>{s}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Weaknesses */}
                        {clause.assessment?.weaknesses?.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-red-400 mb-2">Areas for Improvement</p>
                            <ul className="space-y-1">
                              {clause.assessment.weaknesses.map((w, j) => (
                                <li key={j} className="text-sm text-slate-300 flex items-start gap-2">
                                  <span className="text-red-400 mt-0.5 shrink-0">-</span>
                                  <span>{w}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Observations */}
                        {clause.assessment?.observations?.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-slate-400 mb-2">Observations</p>
                            <ul className="space-y-1">
                              {clause.assessment.observations.map((o, j) => (
                                <li key={j} className="text-sm text-slate-400 flex items-start gap-2">
                                  <span className="text-slate-500 mt-0.5 shrink-0">&bull;</span>
                                  <span>{o}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Coverage meter */}
                        {clause.assessment?.total_provisions_checked > 1 && (
                          <div className="bg-slate-800/80 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-slate-500">Standard provision coverage</span>
                              <span className="text-xs text-slate-400 tabular-nums">{clause.assessment.provision_count}/{clause.assessment.total_provisions_checked} provisions</span>
                            </div>
                            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full transition-all ${
                                clause.assessment.coverage_pct >= 70 ? 'bg-green-500' : clause.assessment.coverage_pct >= 40 ? 'bg-amber-500' : 'bg-red-500'
                              }`} style={{ width: `${clause.assessment.coverage_pct}%` }} />
                            </div>
                          </div>
                        )}

                        {/* Top match */}
                        {clause.top_match && (
                          <div className="bg-slate-800/40 border border-slate-700/30 rounded-lg p-3">
                            <p className="text-xs text-slate-500 mb-2">Most similar clause in corpus:</p>
                            <p className="text-sm text-slate-400 leading-relaxed line-clamp-3">{clause.top_match.preview}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs px-2 py-0.5 rounded bg-slate-700/60 text-slate-400 capitalize">{clause.top_match.category?.replace(/_/g, ' ')}</span>
                              <span className="text-xs text-slate-600">{Math.round(clause.top_match.score * 100)}% match</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">
              Scorecard based on {scorecardResult.corpus_size?.toLocaleString()} clauses from SEC EDGAR filings. Grades reflect pattern similarity to established commercial contracts. Not legal advice.
            </p>
            <button
              onClick={() => handleDownloadReport('scorecard')}
              disabled={downloading}
              className="flex items-center gap-1.5 px-4 py-2 bg-slate-700/60 hover:bg-slate-700 text-white text-sm font-medium rounded-md transition-all border border-slate-600/40 disabled:opacity-50 shrink-0 ml-4"
            >
              <DownloadIcon className="w-4 h-4" />
              {downloading ? 'Generating...' : 'Download Report'}
            </button>
          </div>
        </div>
      )}

      <ClauseDiffModal
        open={!!diffModal}
        onClose={() => setDiffModal(null)}
        userClause={diffModal?.userClause || ''}
        matchedClause={diffModal?.matchedClause || ''}
        matchScore={diffModal?.score}
        matchCategory={diffModal?.category}
      />
    </div>
  );
}

// --- Arbitration Comparison Tab ---

function ArbitrationTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedJurisdiction, setExpandedJurisdiction] = useState(null);
  const [expandedArbClause, setExpandedArbClause] = useState(null);

  const loadComparison = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.getArbitrationComparison();
      setData(result);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  useEffect(() => { loadComparison(); }, []);

  const jurisdictionConfig = {
    us: { label: 'United States', color: 'text-blue-300', bg: 'bg-blue-900/30', flag: 'US' },
    china: { label: 'China-relevant', color: 'text-red-300', bg: 'bg-red-900/30', flag: 'CN' },
    singapore: { label: 'Singapore-relevant', color: 'text-emerald-300', bg: 'bg-emerald-900/30', flag: 'SG' },
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <ScaleIcon className="w-5 h-5 text-slate-400" /> Arbitration Comparison
        </h2>
        <p className="text-slate-400 text-sm mt-1">
          Compare dispute resolution and arbitration clauses across jurisdictions
        </p>
      </div>

      {error && <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg mb-4">{error}</div>}
      {loading && <Skeleton lines={4} />}

      {data && !loading && (
        <div className="space-y-6">
          {/* Venue distribution */}
          <div className="bg-slate-800/60 border border-slate-700/40 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-white mb-4">Arbitration Venue Distribution</h3>
            <p className="text-xs text-slate-500 mb-4">Based on {data.total_analyzed} dispute resolution clauses analyzed</p>
            <div className="space-y-3">
              {Object.entries(data.venue_distribution || {}).map(([venue, count]) => {
                const maxCount = Math.max(...Object.values(data.venue_distribution || {}));
                const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
                return (
                  <div key={venue} className="flex items-center gap-3">
                    <span className="text-sm text-slate-300 w-32 shrink-0">{venue}</span>
                    <div className="flex-1 h-6 bg-slate-700 rounded-lg overflow-hidden">
                      <div
                        className="h-full bg-amber-600/70 rounded-lg flex items-center pl-2 transition-all duration-500"
                        style={{ width: `${Math.max(pct, 8)}%` }}
                      >
                        <span className="text-xs text-white font-medium">{count}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
              {Object.keys(data.venue_distribution || {}).length === 0 && (
                <p className="text-sm text-slate-500">No specific arbitration venues detected. Run scrapers to expand the corpus.</p>
              )}
            </div>
          </div>

          {/* Seat distribution */}
          {Object.keys(data.seat_distribution || {}).length > 0 && (
            <div className="bg-slate-800/60 border border-slate-700/40 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-white mb-4">Arbitration Seat / Governing Law Location</h3>
              <div className="flex flex-wrap gap-3">
                {Object.entries(data.seat_distribution || {}).map(([seat, count]) => (
                  <div key={seat} className="bg-slate-700/50 rounded-lg px-4 py-2 text-center">
                    <p className="text-lg font-bold text-white">{count}</p>
                    <p className="text-xs text-slate-400">{seat}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* By jurisdiction */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-white">Sample Clauses by Jurisdiction</h3>
            {Object.entries(data.by_jurisdiction || {}).map(([jur, clauses]) => {
              const config = jurisdictionConfig[jur] || { label: jur, color: 'text-slate-300', bg: 'bg-slate-800', flag: jur.toUpperCase() };
              if (clauses.length === 0) return null;
              return (
                <div key={jur} className="bg-slate-800/60 border border-slate-700/40 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedJurisdiction(expandedJurisdiction === jur ? null : jur)}
                    className="w-full p-4 flex items-center justify-between hover:bg-slate-750 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-1 rounded ${config.bg} ${config.color} font-medium`}>{config.flag}</span>
                      <span className="text-sm text-white font-medium">{config.label}</span>
                      <span className="text-xs text-slate-500">{clauses.length} clauses</span>
                    </div>
                    <svg className={`w-4 h-4 text-slate-400 transition-transform ${expandedJurisdiction === jur ? 'rotate-90' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6" /></svg>
                  </button>
                  {expandedJurisdiction === jur && (
                    <div className="border-t border-slate-700 divide-y divide-slate-700/50">
                      {clauses.map((c, i) => {
                        const aKey = `arb-${jur}-${i}`;
                        const isExp = expandedArbClause === aKey;
                        return (
                          <button key={i} onClick={() => setExpandedArbClause(isExp ? null : aKey)}
                            className="w-full text-left p-4 hover:bg-slate-800/60 transition-colors cursor-pointer">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-900/40 text-amber-300">{c.category?.replace(/_/g, ' ')}</span>
                              {c.venues?.map(v => (
                                <span key={v} className="text-xs px-2 py-0.5 rounded bg-slate-700 text-slate-300">{v}</span>
                              ))}
                              {c.seats?.map(s => (
                                <span key={s} className="text-xs px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-400">{s}</span>
                              ))}
                            </div>
                            <p className={`text-sm text-slate-400 leading-relaxed ${isExp ? '' : 'line-clamp-3'}`}>{c.preview}</p>
                            {isExp && c.full_text && (
                              <pre className="mt-2 pt-2 border-t border-slate-700 text-sm text-slate-300 whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto">{c.full_text}</pre>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-xs text-slate-500">
            Analysis based on {data.total_analyzed} arbitration and dispute resolution clauses from publicly filed contracts.
            Distribution will expand as SGX and HKEX filings are indexed.
          </p>
        </div>
      )}
    </div>
  );
}

// --- Vault Tab ---

function VaultTab({ user, onUpgrade }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadModal, setUploadModal] = useState(null); // file object
  const [downloadModal, setDownloadModal] = useState(null); // file row
  const [deleteModal, setDeleteModal] = useState(null); // file row

  const loadFiles = useCallback(async () => {
    try {
      const data = await api.listFiles();
      setFiles(data);
    } catch (err) {
      if (user.tier === 'free') setError('File storage requires a Pro subscription');
      else setError(err.message);
    }
    setLoading(false);
  }, [user.tier]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  if (user.tier === 'free') {
    return (
      <div className="text-center py-16">
        <div className="w-14 h-14 mx-auto mb-4 bg-slate-800/60 border border-slate-700/40 rounded-lg flex items-center justify-center">
          <LockIcon className="w-6 h-6 text-slate-400" />
        </div>
        <h2 className="text-lg font-semibold text-white mb-2">Encrypted Vault</h2>
        <p className="text-slate-400 text-sm mb-8 max-w-md mx-auto">
          Store up to 1,200 contracts with AES-256-GCM encryption. Only you can decrypt them — we never see your files.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={onUpgrade('annual')}
            className="px-6 py-2.5 bg-amber-600 hover:bg-amber-500 text-white font-medium rounded-md transition-all active:scale-[0.97]">
            S$90/mo
          </button>
          <button onClick={onUpgrade('monthly')}
            className="px-6 py-2.5 border border-slate-600 text-slate-300 hover:bg-slate-700 font-medium rounded-md transition-colors">
            S$180 / 6 mo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <LockIcon className="w-5 h-5 text-slate-400" /> Encrypted Vault
          </h2>
          <p className="text-slate-400 text-sm mt-1">{files.length} / 1,200 files stored</p>
        </div>
        <label className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium rounded-md cursor-pointer transition-all active:scale-[0.97] flex items-center gap-2">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17,8 12,3 7,8" /><line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          Upload
          <input type="file" className="hidden" accept=".pdf,.doc,.docx,.txt" onChange={e => {
            const file = e.target.files?.[0];
            if (file) setUploadModal(file);
            e.target.value = '';
          }} />
        </label>
      </div>

      {error && <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg mb-4">{error}</div>}
      {loading ? <Skeleton lines={3} /> : (
        <div className="space-y-2">
          {files.length === 0 && (
            <div className="text-center py-12 border-2 border-dashed border-slate-700/50 rounded-lg">
              <FileTextIcon className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500">No files yet. Upload your first contract.</p>
            </div>
          )}
          {files.map(f => (
            <div key={f.id} className="bg-slate-800/60 border border-slate-700/40 rounded-lg p-4 flex items-center gap-4 hover:border-slate-600/50 transition-all card-hover">
              <FileIcon filename={f.filename} className="w-8 h-8 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium truncate">{f.filename}</p>
                <p className="text-xs text-slate-500">{(f.file_size / 1024).toFixed(1)} KB &middot; {new Date(f.uploaded_at).toLocaleDateString()}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => setDownloadModal(f)}
                  className="p-2 text-slate-400 hover:text-amber-400 hover:bg-slate-700 rounded-lg transition-colors" title="Download & Decrypt">
                  <DownloadIcon className="w-4 h-4" />
                </button>
                <button onClick={() => setDeleteModal(f)}
                  className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded-lg transition-colors" title="Delete">
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload password modal */}
      <PasswordModal
        open={!!uploadModal}
        onClose={() => setUploadModal(null)}
        title="Encrypt & Upload"
        submitLabel="Upload"
        onSubmit={async (pw) => {
          try {
            await api.uploadFile(uploadModal, pw);
            setUploadModal(null);
            loadFiles();
          } catch (err) { alert(err.message); }
        }}
      />

      {/* Download password modal */}
      <PasswordModal
        open={!!downloadModal}
        onClose={() => setDownloadModal(null)}
        title="Decrypt & Download"
        submitLabel="Download"
        onSubmit={async (pw) => {
          try {
            const res = await fetch(`/api/files/${downloadModal.id}/download`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ password: pw }),
            });
            if (!res.ok) {
              const err = await res.json();
              alert(err.error || 'Download failed');
              return;
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = downloadModal.filename;
            a.click();
            URL.revokeObjectURL(url);
            setDownloadModal(null);
          } catch { alert('Download failed'); }
        }}
      />

      {/* Delete confirm modal */}
      <ConfirmModal
        open={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        title="Delete File"
        message={`Are you sure you want to delete "${deleteModal?.filename}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={async () => {
          await api.deleteFile(deleteModal.id);
          setDeleteModal(null);
          loadFiles();
        }}
      />
    </div>
  );
}

// --- Dashboard Tab ---

// --- Library Tab (Saved Clause Library) ---

function LibraryTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFolder, setActiveFolder] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [editFolder, setEditFolder] = useState('');
  const [deleteModal, setDeleteModal] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [newFolder, setNewFolder] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);

  const loadLibrary = useCallback(async () => {
    try {
      const result = await api.listSavedClauses(activeFolder);
      setData(result);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [activeFolder]);

  useEffect(() => { setLoading(true); loadLibrary(); }, [loadLibrary]);

  const handleDelete = async (id) => {
    try {
      await api.deleteSavedClause(id);
      setDeleteModal(null);
      loadLibrary();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdate = async (id) => {
    try {
      await api.updateSavedClause(id, { notes: editNotes, folder: editFolder });
      setEditingId(null);
      loadLibrary();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleMoveToFolder = async (id, folder) => {
    try {
      await api.updateSavedClause(id, { folder });
      loadLibrary();
    } catch (err) {
      setError(err.message);
    }
  };

  const allFolders = data?.folders || [];
  const existingFolderNames = allFolders.map(f => f.folder);

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <BookIcon className="w-5 h-5 text-slate-400" /> Clause Library
        </h2>
        <p className="text-slate-400 text-sm mt-1">
          Your personal precedent bank — {data?.total || 0} clauses saved
        </p>
      </div>

      {error && <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg mb-4">{error}</div>}

      {/* Folder navigation */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <button
          onClick={() => setActiveFolder(null)}
          className={`text-xs px-3 py-1.5 rounded-md transition-all flex items-center gap-1.5 ${
            !activeFolder ? 'bg-amber-600 text-white' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60 border border-slate-700/40'
          }`}
        >
          All ({data?.total || 0})
        </button>
        {allFolders.map(f => (
          <button key={f.folder} onClick={() => setActiveFolder(f.folder)}
            className={`text-xs px-3 py-1.5 rounded-md transition-all flex items-center gap-1.5 ${
              activeFolder === f.folder ? 'bg-amber-600 text-white' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60 border border-slate-700/40'
            }`}
          >
            <FolderIcon className="w-3 h-3" />
            {f.folder} ({f.count})
          </button>
        ))}
        <button
          onClick={() => setShowNewFolder(!showNewFolder)}
          className="text-xs px-2.5 py-1.5 rounded-md bg-slate-800/40 text-slate-500 hover:text-white hover:bg-slate-700/60 border border-dashed border-slate-700/40 transition-all"
        >
          + New Folder
        </button>
      </div>

      {showNewFolder && (
        <div className="mb-4 flex items-center gap-2">
          <input
            value={newFolder}
            onChange={e => setNewFolder(e.target.value)}
            placeholder="Folder name..."
            className="px-3 py-2 bg-slate-800/80 border border-slate-700/60 rounded-md text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30 w-48"
          />
          <p className="text-xs text-slate-500">Save a clause to this folder to create it</p>
        </div>
      )}

      {loading && <Skeleton lines={3} />}

      {data && !loading && data.clauses.length === 0 && (
        <div className="text-center py-16">
          <div className="w-14 h-14 mx-auto mb-4 bg-slate-800/60 border border-slate-700/40 rounded-lg flex items-center justify-center">
            <BookmarkIcon className="w-6 h-6 text-slate-400" />
          </div>
          <h3 className="text-base font-medium text-white mb-2">No saved clauses yet</h3>
          <p className="text-slate-400 text-sm max-w-md mx-auto">
            Search for clauses in the Discovery tab and click "Save to Library" to start building your personal precedent bank.
          </p>
        </div>
      )}

      {data && !loading && data.clauses.length > 0 && (
        <div className="space-y-3">
          {data.clauses.map(clause => (
            <div key={clause.id} className="bg-slate-800/60 border border-slate-700/40 rounded-lg overflow-hidden hover:border-slate-600/50 transition-all">
              {/* Header — click to expand */}
              <button
                onClick={() => setExpandedId(expandedId === clause.id ? null : clause.id)}
                className="w-full text-left p-4"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-700/80 text-slate-300 capitalize">
                      {clause.category?.replace(/_/g, ' ') || 'uncategorized'}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded bg-slate-700/50 text-slate-500">
                      <FolderIcon className="w-3 h-3 inline mr-1" />{clause.folder}
                    </span>
                    {clause.score && (
                      <span className="text-xs text-slate-500 tabular-nums">{Math.round(clause.score * 100)}% match</span>
                    )}
                  </div>
                  <span className="text-xs text-slate-600">{new Date(clause.saved_at).toLocaleDateString()}</span>
                </div>
                <p className="text-sm text-slate-300 leading-relaxed line-clamp-2">{clause.preview}</p>
                {clause.notes && (
                  <p className="text-xs text-amber-400/70 mt-2 italic line-clamp-1">Note: {clause.notes}</p>
                )}
              </button>

              {/* Expanded view */}
              {expandedId === clause.id && (
                <div className="border-t border-slate-700 p-5 bg-slate-850">
                  {clause.full_text && (
                    <pre className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed max-h-72 overflow-y-auto mb-4">{clause.full_text}</pre>
                  )}

                  {/* Edit mode */}
                  {editingId === clause.id ? (
                    <div className="space-y-3 mb-4 bg-slate-800/80 rounded-lg p-4 border border-slate-700/40">
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Notes</label>
                        <textarea
                          value={editNotes}
                          onChange={e => setEditNotes(e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 resize-none"
                          placeholder="Add your notes about this clause..."
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Folder</label>
                        <div className="flex gap-2">
                          <select
                            value={editFolder}
                            onChange={e => setEditFolder(e.target.value)}
                            className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                          >
                            {existingFolderNames.map(f => (
                              <option key={f} value={f}>{f}</option>
                            ))}
                            {newFolder && !existingFolderNames.includes(newFolder) && (
                              <option value={newFolder}>{newFolder} (new)</option>
                            )}
                          </select>
                          {newFolder && !existingFolderNames.includes(newFolder) && (
                            <button
                              onClick={() => setEditFolder(newFolder)}
                              className="text-xs px-3 py-2 bg-amber-600/20 text-amber-400 rounded-md border border-amber-700/30"
                            >
                              Use "{newFolder}"
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleUpdate(clause.id)}
                          className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium rounded-md transition-all"
                        >
                          Save Changes
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="px-4 py-2 border border-slate-600 text-slate-300 text-sm rounded-md hover:bg-slate-700 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => { setEditingId(clause.id); setEditNotes(clause.notes || ''); setEditFolder(clause.folder || 'General'); }}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-slate-700/60 text-slate-400 hover:text-white rounded-md transition-all border border-slate-600/40"
                      >
                        <EditIcon className="w-3.5 h-3.5" /> Edit Notes
                      </button>
                      {existingFolderNames.filter(f => f !== clause.folder).length > 0 && (
                        <select
                          value=""
                          onChange={e => { if (e.target.value) handleMoveToFolder(clause.id, e.target.value); }}
                          className="text-xs px-2 py-1.5 bg-slate-700/60 border border-slate-600/40 rounded-md text-slate-400 focus:outline-none"
                        >
                          <option value="">Move to...</option>
                          {existingFolderNames.filter(f => f !== clause.folder).map(f => (
                            <option key={f} value={f}>{f}</option>
                          ))}
                        </select>
                      )}
                      <button
                        onClick={() => setDeleteModal(clause)}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-red-900/30 text-red-400 hover:bg-red-900/50 rounded-md transition-all border border-red-800/30"
                      >
                        <TrashIcon className="w-3.5 h-3.5" /> Remove
                      </button>
                      <span className="text-xs text-slate-600 ml-auto">{clause.clause_id}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        open={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        onConfirm={() => handleDelete(deleteModal?.id)}
        title="Remove from Library"
        message={`Remove this ${deleteModal?.category?.replace(/_/g, ' ') || ''} clause from your library? This cannot be undone.`}
        confirmLabel="Remove"
        danger
      />
    </div>
  );
}


// --- IP Cross-Reference Tab (Max Tier) ---

function IPTab({ user }) {
  const [mode, setMode] = useState('search'); // 'search' or 'crossref'
  const [query, setQuery] = useState('');
  const [clauseText, setClauseText] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedPatentId, setExpandedPatentId] = useState(null);

  const handlePatentSearch = async (q) => {
    const searchQuery = q || query;
    if (!searchQuery.trim()) return;
    setQuery(searchQuery);
    setLoading(true); setError('');
    try {
      const data = await api.ipSearch(searchQuery);
      setResult({ type: 'search', data });
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const handleCrossRef = async () => {
    if (clauseText.trim().length < 20) return;
    setLoading(true); setError('');
    try {
      const data = await api.ipCrossReference(clauseText);
      setResult({ type: 'crossref', data });
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const suggestions = [
    'machine learning neural network',
    'pharmaceutical composition antibody',
    'semiconductor fabrication process',
    'payment processing electronic commerce',
    'wireless communication 5G protocol',
  ];

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <ShieldIcon className="w-5 h-5 text-amber-400" /> IP Cross-Reference
        </h2>
        <p className="text-slate-400 text-sm mt-1">
          Cross-reference contract clauses against {result?.data?.patent_corpus_size || '155'} USPTO patent filings
        </p>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-1 mb-4 bg-slate-800/40 rounded-lg p-1 w-fit">
        <button onClick={() => { setMode('search'); setResult(null); }}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${mode === 'search' ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white'}`}>
          Patent Search
        </button>
        <button onClick={() => { setMode('crossref'); setResult(null); }}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${mode === 'crossref' ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white'}`}>
          Cross-Reference
        </button>
      </div>

      {error && <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg mb-4">{error}</div>}

      {/* Patent Search mode */}
      {mode === 'search' && (
        <>
          <form onSubmit={e => { e.preventDefault(); handlePatentSearch(); }} className="mb-4">
            <div className="flex gap-3">
              <input value={query} onChange={e => setQuery(e.target.value)}
                placeholder="Search patents by technology, method, or invention..."
                className="flex-1 px-4 py-2.5 bg-slate-800/80 border border-slate-700/60 rounded-md text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
              <button type="submit" disabled={loading || !query.trim()}
                className="px-6 py-2.5 bg-amber-600 hover:bg-amber-500 disabled:bg-amber-800 text-white font-medium rounded-md transition-all text-sm shrink-0">
                {loading ? 'Searching...' : 'Search Patents'}
              </button>
            </div>
          </form>
          {!result && !loading && (
            <div className="mb-6">
              <p className="text-xs text-slate-500 mb-2">Try searching:</p>
              <div className="flex flex-wrap gap-2">
                {suggestions.map(s => (
                  <button key={s} onClick={() => handlePatentSearch(s)}
                    className="text-xs px-3 py-1.5 bg-slate-800/40 border border-slate-700/40 rounded-md text-slate-400 hover:text-white hover:border-slate-600/60 transition-all">{s}</button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Cross-Reference mode */}
      {mode === 'crossref' && (
        <div className="mb-4">
          <textarea value={clauseText} onChange={e => setClauseText(e.target.value)} rows={6}
            className="w-full px-4 py-3 bg-slate-800/80 border border-slate-700/60 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30 text-sm leading-relaxed"
            placeholder="Paste your IP-related contract clause here (technology licensing, IP assignment, patent indemnification, etc.) to find matching patents..." />
          <button onClick={handleCrossRef} disabled={loading || clauseText.trim().length < 20}
            className="mt-3 px-6 py-2.5 bg-amber-600 hover:bg-amber-500 disabled:bg-amber-800 text-white font-medium rounded-md transition-all text-sm">
            {loading ? 'Analyzing...' : 'Cross-Reference'}
          </button>
        </div>
      )}

      {loading && <Skeleton lines={3} />}

      {/* Patent search results */}
      {result?.type === 'search' && !loading && (
        <div className="space-y-3">
          <p className="text-xs text-slate-500">{result.data.results?.length || 0} patents found from {result.data.total_indexed} indexed</p>
          {result.data.results?.map((p, i) => {
            const pKey = p.patent_id || `p-${i}`;
            const isExpanded = expandedPatentId === pKey;
            return (
              <button key={pKey} onClick={() => setExpandedPatentId(isExpanded ? null : pKey)}
                className="w-full text-left bg-slate-800/60 border border-slate-700/40 rounded-lg p-4 hover:border-slate-600/50 transition-all cursor-pointer">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium px-2 py-0.5 rounded bg-amber-900/30 text-amber-300">{p.patent_id}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-slate-700/60 text-slate-400">{p.category}</span>
                  </div>
                  <span className="text-xs text-slate-500 tabular-nums">{Math.round(p.score * 100)}% match</span>
                </div>
                <h4 className="text-sm font-medium text-white mb-2">{p.title}</h4>
                <p className={`text-xs text-slate-400 leading-relaxed ${isExpanded ? '' : 'line-clamp-3'}`}>{p.preview}</p>
                {isExpanded && p.full_text && (
                  <pre className="mt-2 pt-2 border-t border-slate-700 text-xs text-slate-300 whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto">{p.full_text}</pre>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Cross-reference results */}
      {result?.type === 'crossref' && !loading && (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Contract clauses column */}
            <div>
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                Matching Contract Clauses ({result.data.clause_corpus_size?.toLocaleString()} corpus)
              </h3>
              <div className="space-y-2">
                {result.data.clause_results?.map((c, i) => {
                  const cKey = `xref-c-${i}`;
                  const isExp = expandedPatentId === cKey;
                  return (
                    <button key={cKey} onClick={() => setExpandedPatentId(isExp ? null : cKey)}
                      className="w-full text-left bg-slate-800/60 border border-slate-700/40 rounded-lg p-3 hover:bg-slate-700/40 transition-colors cursor-pointer">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-700/60 text-slate-300 capitalize">{c.category?.replace(/_/g, ' ')}</span>
                        <span className="text-xs text-slate-500">{Math.round(c.score * 100)}%</span>
                      </div>
                      <p className={`text-xs text-slate-400 ${isExp ? '' : 'line-clamp-3'}`}>{c.preview}</p>
                      {isExp && c.full_text && (
                        <pre className="mt-2 pt-2 border-t border-slate-700 text-xs text-slate-300 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">{c.full_text}</pre>
                      )}
                    </button>
                  );
                })}
                {result.data.clause_results?.length === 0 && <p className="text-xs text-slate-600 italic">No matching contract clauses</p>}
              </div>
            </div>

            {/* Patent column */}
            <div>
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500" />
                Related Patents ({result.data.patent_corpus_size} indexed)
              </h3>
              <div className="space-y-2">
                {result.data.patent_results?.map((p, i) => {
                  const pKey = `xref-p-${i}`;
                  const isExp = expandedPatentId === pKey;
                  return (
                    <button key={pKey} onClick={() => setExpandedPatentId(isExp ? null : pKey)}
                      className="w-full text-left bg-slate-800/60 border border-blue-800/30 rounded-lg p-3 hover:bg-slate-700/40 transition-colors cursor-pointer">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs px-2 py-0.5 rounded bg-blue-900/30 text-blue-300">{p.patent_id}</span>
                        <span className="text-xs text-slate-500">{Math.round(p.score * 100)}%</span>
                      </div>
                      <p className="text-xs text-white font-medium mb-1">{p.title}</p>
                      <p className={`text-xs text-slate-400 ${isExp ? '' : 'line-clamp-2'}`}>{p.preview}</p>
                      {isExp && p.full_text && (
                        <pre className="mt-2 pt-2 border-t border-slate-700 text-xs text-slate-300 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">{p.full_text}</pre>
                      )}
                    </button>
                  );
                })}
                {result.data.patent_results?.length === 0 && <p className="text-xs text-slate-600 italic">No matching patents found</p>}
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-500">
            Cross-referencing {result.data.clause_corpus_size?.toLocaleString()} contract clauses with {result.data.patent_corpus_size} USPTO patents. Not legal advice.
          </p>
        </div>
      )}
    </div>
  );
}


// --- Deal Structure Templates Tab ---

function TemplatesTab() {
  const [templates, setTemplates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [templateData, setTemplateData] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [expandedClauseKey, setExpandedClauseKey] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const data = await api.listTemplates();
        setTemplates(data);
      } catch (err) { setError(err.message); }
      setLoading(false);
    })();
  }, []);

  const handleSelectTemplate = async (id) => {
    setSelectedId(id);
    setLoadingTemplate(true);
    setTemplateData(null);
    setExpandedSection(null);
    setError('');
    try {
      const data = await api.getTemplate(id);
      setTemplateData(data);
    } catch (err) { setError(err.message); }
    setLoadingTemplate(false);
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <FileTextIcon className="w-5 h-5 text-slate-400" /> Deal Structure Templates
        </h2>
        <p className="text-slate-400 text-sm mt-1">Pre-built clause packages from real filings — see how actual deals were structured</p>
      </div>

      {error && <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg mb-4">{error}</div>}
      {loading && <Skeleton lines={3} />}

      {/* Template selector */}
      {templates && (
        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          {templates.map(t => (
            <button key={t.id} onClick={() => handleSelectTemplate(t.id)}
              className={`text-left p-5 rounded-lg border transition-all ${
                selectedId === t.id
                  ? 'bg-amber-900/20 border-amber-600/30'
                  : 'bg-slate-800/60 border-slate-700/40 hover:border-slate-600/50'
              }`}>
              <h3 className="text-sm font-semibold text-white mb-1">{t.name}</h3>
              <p className="text-xs text-slate-400 mb-3 line-clamp-2">{t.description}</p>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-0.5 rounded bg-slate-700/60 text-slate-400">{t.section_count} clauses</span>
                <div className="flex flex-wrap gap-1">
                  {t.sections.slice(0, 3).map(s => (
                    <span key={s} className="text-xs text-slate-600">{s}</span>
                  ))}
                  {t.sections.length > 3 && <span className="text-xs text-slate-600">+{t.sections.length - 3} more</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {loadingTemplate && <Skeleton lines={5} />}

      {/* Template detail */}
      {templateData && !loadingTemplate && (
        <div className="space-y-3">
          <div className="bg-slate-800/40 border border-slate-700/30 rounded-lg p-4 flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-white">{templateData.name}</h3>
              <p className="text-xs text-slate-400 mt-1">{templateData.description}</p>
            </div>
            <span className="text-xs text-slate-500">{templateData.sections.length} sections from {templateData.corpus_size?.toLocaleString()} filings</span>
          </div>

          {templateData.sections.map((section, i) => {
            const isExpanded = expandedSection === i;
            return (
              <div key={i} className="bg-slate-800/60 border border-slate-700/40 rounded-lg overflow-hidden">
                <button onClick={() => setExpandedSection(isExpanded ? null : i)}
                  className="w-full text-left p-4 hover:bg-slate-800/80 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold text-amber-500 w-6">{i + 1}.</span>
                      <span className="text-sm font-medium text-white">{section.name}</span>
                      {section.category && (
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-700/60 text-slate-400 capitalize">{section.category.replace(/_/g, ' ')}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{section.clause_count} examples</span>
                      <svg className={`w-4 h-4 text-slate-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6" /></svg>
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-slate-700 p-4 space-y-3">
                    {section.best_clause ? (() => {
                      const bestKey = `tmpl-${i}-best`;
                      const isBestExp = expandedClauseKey === bestKey;
                      return (
                        <>
                          <div>
                            <p className="text-xs text-amber-400 font-medium mb-2">Best Match</p>
                            <button onClick={() => setExpandedClauseKey(isBestExp ? null : bestKey)}
                              className="w-full text-left bg-slate-800/80 rounded-lg p-4 hover:bg-slate-700/60 transition-colors cursor-pointer">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs px-2 py-0.5 rounded bg-slate-700/80 text-slate-300 capitalize">{section.best_clause.category?.replace(/_/g, ' ')}</span>
                                <span className="text-xs text-slate-500">{Math.round(section.best_clause.score * 100)}% match</span>
                              </div>
                              <p className={`text-sm text-slate-300 leading-relaxed ${isBestExp ? 'whitespace-pre-wrap' : 'line-clamp-3'}`}>{section.best_clause.preview}</p>
                              {isBestExp && section.best_clause.full_text && (
                                <pre className="mt-2 pt-2 border-t border-slate-700 text-xs text-slate-300 whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto">{section.best_clause.full_text}</pre>
                              )}
                            </button>
                          </div>
                          {section.alternatives?.length > 0 && (
                            <div>
                              <p className="text-xs text-slate-500 font-medium mb-2">Alternative Drafting</p>
                              {section.alternatives.map((alt, j) => {
                                const altKey = `tmpl-${i}-alt-${j}`;
                                const isAltExp = expandedClauseKey === altKey;
                                return (
                                  <button key={j} onClick={() => setExpandedClauseKey(isAltExp ? null : altKey)}
                                    className="w-full text-left bg-slate-800/40 rounded-lg p-3 mb-2 hover:bg-slate-700/40 transition-colors cursor-pointer">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-xs text-slate-500">{Math.round(alt.score * 100)}% match</span>
                                    </div>
                                    <p className={`text-xs text-slate-400 leading-relaxed ${isAltExp ? '' : 'line-clamp-3'}`}>{alt.preview}</p>
                                    {isAltExp && alt.full_text && (
                                      <pre className="mt-2 pt-2 border-t border-slate-700 text-xs text-slate-300 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">{alt.full_text}</pre>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </>
                      );
                    })() : (
                      <p className="text-xs text-slate-500 italic">No matching clauses found for this section.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// --- Clause Version Tracking Tab ---

function EvolutionTab() {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedYear, setExpandedYear] = useState(null);
  const [expandedEvoClause, setExpandedEvoClause] = useState(null);

  const suggestions = [
    'limitation of liability aggregate cap',
    'force majeure pandemic exclusion',
    'non-compete executive employment',
    'indemnification hold harmless',
    'arbitration SIAC Singapore',
    'confidentiality survival period',
  ];

  const handleSearch = async (q) => {
    const searchQuery = q || query;
    if (!searchQuery.trim()) return;
    setQuery(searchQuery);
    setLoading(true);
    setError('');
    try {
      const data = await api.clauseEvolution(searchQuery);
      setResult(data);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <HistoryIcon className="w-5 h-5 text-slate-400" /> Clause Version Tracking
        </h2>
        <p className="text-slate-400 text-sm mt-1">See how clause language evolved over time across filings</p>
      </div>

      <form onSubmit={e => { e.preventDefault(); handleSearch(); }} className="mb-6">
        <div className="flex gap-3">
          <input value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Enter a clause type to track its evolution..."
            className="flex-1 px-4 py-2.5 bg-slate-800/80 border border-slate-700/60 rounded-md text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
          <button type="submit" disabled={loading || !query.trim()}
            className="px-6 py-2.5 bg-amber-600 hover:bg-amber-500 disabled:bg-amber-800 text-white font-medium rounded-md transition-all text-sm shrink-0">
            {loading ? 'Tracking...' : 'Track Evolution'}
          </button>
        </div>
      </form>

      {!result && !loading && (
        <div className="mb-6">
          <p className="text-xs text-slate-500 mb-2">Try tracking:</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map(s => (
              <button key={s} onClick={() => handleSearch(s)}
                className="text-xs px-3 py-1.5 bg-slate-800/40 border border-slate-700/40 rounded-md text-slate-400 hover:text-white hover:border-slate-600/60 transition-all">
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg mb-4">{error}</div>}
      {loading && <Skeleton lines={4} />}

      {result && !loading && (
        <div className="space-y-4">
          {/* Summary */}
          <div className="bg-slate-800/40 border border-slate-700/30 rounded-lg p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <span className="text-xs text-slate-500">Category</span>
                <p className="text-sm text-white capitalize">{result.detected_category?.replace(/_/g, ' ') || 'Mixed'}</p>
              </div>
              <div>
                <span className="text-xs text-slate-500">Total Clauses</span>
                <p className="text-sm text-white">{result.total_clauses}</p>
              </div>
              <div>
                <span className="text-xs text-slate-500">Time Periods</span>
                <p className="text-sm text-white">{result.year_count}</p>
              </div>
            </div>
          </div>

          {/* Word count trend */}
          {Object.keys(result.avg_word_counts || {}).length > 1 && (
            <div className="bg-slate-800/60 border border-slate-700/40 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-white mb-3">Average Clause Length Over Time</h3>
              <div className="flex items-end gap-2 h-32">
                {Object.entries(result.avg_word_counts).map(([year, wc]) => {
                  const maxWc = Math.max(...Object.values(result.avg_word_counts));
                  const pct = maxWc > 0 ? (wc / maxWc) * 100 : 0;
                  return (
                    <div key={year} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-xs text-slate-400 tabular-nums">{wc}</span>
                      <div className="w-full bg-slate-700 rounded-t" style={{ height: `${Math.max(pct, 5)}%` }}>
                        <div className="w-full h-full bg-amber-600/60 rounded-t" />
                      </div>
                      <span className="text-xs text-slate-500">{year}</span>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-600 mt-2">Words per clause (average)</p>
            </div>
          )}

          {/* Year-by-year breakdown */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-3">Clauses by Filing Period</h3>
            <div className="space-y-2">
              {Object.entries(result.years || {}).map(([year, clauses]) => (
                <div key={year} className="bg-slate-800/60 border border-slate-700/40 rounded-lg overflow-hidden">
                  <button onClick={() => setExpandedYear(expandedYear === year ? null : year)}
                    className="w-full text-left p-4 hover:bg-slate-800/80 transition-colors flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-amber-400 tabular-nums w-16">{year}</span>
                      <span className="text-sm text-white">{clauses.length} clause{clauses.length !== 1 ? 's' : ''}</span>
                    </div>
                    <svg className={`w-4 h-4 text-slate-500 transition-transform ${expandedYear === year ? 'rotate-90' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6" /></svg>
                  </button>
                  {expandedYear === year && (
                    <div className="border-t border-slate-700 p-4 space-y-2">
                      {clauses.map((c, i) => {
                        const eKey = `evo-${year}-${i}`;
                        const isExp = expandedEvoClause === eKey;
                        return (
                          <button key={i} onClick={() => setExpandedEvoClause(isExp ? null : eKey)}
                            className="w-full text-left bg-slate-800/80 rounded p-3 hover:bg-slate-700/60 transition-colors cursor-pointer">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs px-2 py-0.5 rounded bg-slate-700/60 text-slate-400 capitalize">{c.category?.replace(/_/g, ' ')}</span>
                              <span className="text-xs text-slate-500">{Math.round(c.score * 100)}% match</span>
                              <span className="text-xs text-slate-600">{c.word_count} words</span>
                            </div>
                            <p className={`text-xs text-slate-400 leading-relaxed ${isExp ? '' : 'line-clamp-3'}`}>{c.preview}</p>
                            {isExp && c.full_text && (
                              <pre className="mt-2 pt-2 border-t border-slate-700 text-xs text-slate-300 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">{c.full_text}</pre>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <p className="text-xs text-slate-500">
            Based on {result.corpus_size?.toLocaleString()} clauses. Year detection is based on filing dates found in clause text. Not all clauses have identifiable dates.
          </p>
        </div>
      )}
    </div>
  );
}


// --- Clause Alerts Tab ---

function AlertsTab() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newKeywords, setNewKeywords] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newJurisdiction, setNewJurisdiction] = useState('all');
  const [checkingId, setCheckingId] = useState(null);
  const [matchResults, setMatchResults] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [expandedMatchId, setExpandedMatchId] = useState(null);

  const categories = ['', 'confidentiality', 'definitions', 'dispute_resolution', 'force_majeure', 'governing_law', 'indemnity', 'limitation_of_liability', 'payment', 'tax', 'termination'];

  const loadAlerts = useCallback(async () => {
    try {
      const data = await api.listAlerts();
      setAlerts(data);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim() || !newKeywords.trim()) return;
    try {
      await api.createAlert({ name: newName, keywords: newKeywords, category: newCategory, jurisdiction: newJurisdiction });
      setShowCreate(false);
      setNewName(''); setNewKeywords(''); setNewCategory(''); setNewJurisdiction('all');
      loadAlerts();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCheck = async (alertId) => {
    setCheckingId(alertId);
    setMatchResults(null);
    try {
      const data = await api.checkAlert(alertId);
      setMatchResults({ alertId, ...data });
      loadAlerts();
    } catch (err) {
      setError(err.message);
    }
    setCheckingId(null);
  };

  const handleToggle = async (alert) => {
    try {
      await api.updateAlert(alert.id, { active: !alert.active });
      loadAlerts();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteAlert(id);
      setDeleteModal(null);
      loadAlerts();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <BellIcon className="w-5 h-5 text-slate-400" /> Clause Alerts
          </h2>
          <p className="text-slate-400 text-sm mt-1">Get notified when new filings match your practice area</p>
        </div>
        <button onClick={() => setShowCreate(!showCreate)}
          className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium rounded-md transition-all">
          + New Alert
        </button>
      </div>

      {error && <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg mb-4">{error}</div>}

      {/* Create form */}
      {showCreate && (
        <form onSubmit={handleCreate} className="bg-slate-800/60 border border-slate-700/40 rounded-lg p-5 mb-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Alert Name</label>
              <input value={newName} onChange={e => setNewName(e.target.value)}
                placeholder="e.g. VIE Force Majeure updates"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Keywords (search query)</label>
              <input value={newKeywords} onChange={e => setNewKeywords(e.target.value)}
                placeholder="e.g. force majeure pandemic VIE"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Category (optional)</label>
              <select value={newCategory} onChange={e => setNewCategory(e.target.value)}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30">
                <option value="">All categories</option>
                {categories.filter(Boolean).map(c => (
                  <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Jurisdiction</label>
              <select value={newJurisdiction} onChange={e => setNewJurisdiction(e.target.value)}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30">
                <option value="all">All jurisdictions</option>
                <option value="china">China-tagged</option>
                <option value="singapore">Singapore</option>
                <option value="crossborder">Cross-Border</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={!newName.trim() || !newKeywords.trim()}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-amber-800 text-white text-sm font-medium rounded-md transition-all">
              Create Alert
            </button>
            <button type="button" onClick={() => setShowCreate(false)}
              className="px-4 py-2 border border-slate-600 text-slate-300 text-sm rounded-md hover:bg-slate-700 transition-colors">
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading && <Skeleton lines={2} />}

      {!loading && alerts.length === 0 && !showCreate && (
        <div className="text-center py-16">
          <div className="w-14 h-14 mx-auto mb-4 bg-slate-800/60 border border-slate-700/40 rounded-lg flex items-center justify-center">
            <BellIcon className="w-6 h-6 text-slate-400" />
          </div>
          <h3 className="text-base font-medium text-white mb-2">No alerts set up yet</h3>
          <p className="text-slate-400 text-sm max-w-md mx-auto mb-6">
            Create alerts to monitor for new clauses matching your practice area. Check them anytime to find new relevant filings.
          </p>
          <button onClick={() => setShowCreate(true)}
            className="px-5 py-2.5 bg-amber-600 hover:bg-amber-500 text-white font-medium rounded-md transition-all text-sm">
            Create Your First Alert
          </button>
        </div>
      )}

      {/* Alert list */}
      {alerts.length > 0 && (
        <div className="space-y-3">
          {alerts.map(alert => (
            <div key={alert.id} className={`bg-slate-800/60 border rounded-lg p-4 transition-all ${alert.active ? 'border-slate-700/40' : 'border-slate-800/30 opacity-60'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <h4 className="text-sm font-medium text-white">{alert.name}</h4>
                  {alert.unseen_matches > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-600 text-white font-medium animate-pulse">
                      {alert.unseen_matches} new
                    </span>
                  )}
                  {!alert.active && <span className="text-xs text-slate-600">Paused</span>}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleCheck(alert.id)} disabled={checkingId === alert.id}
                    className="text-xs px-3 py-1.5 bg-amber-600/20 text-amber-400 hover:bg-amber-600/30 rounded-md transition-all border border-amber-700/30 disabled:opacity-50">
                    {checkingId === alert.id ? 'Checking...' : 'Check Now'}
                  </button>
                  <button onClick={() => handleToggle(alert)}
                    className="text-xs px-2.5 py-1.5 bg-slate-700/60 text-slate-400 hover:text-white rounded-md transition-all">
                    {alert.active ? 'Pause' : 'Resume'}
                  </button>
                  <button onClick={() => setDeleteModal(alert)}
                    className="text-xs px-2.5 py-1.5 bg-red-900/30 text-red-400 hover:bg-red-900/50 rounded-md transition-all">
                    Delete
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <span className="px-2 py-0.5 bg-slate-700/40 rounded">{alert.keywords}</span>
                {alert.category && <span className="px-2 py-0.5 bg-slate-700/40 rounded capitalize">{alert.category.replace(/_/g, ' ')}</span>}
                <span className="px-2 py-0.5 bg-slate-700/40 rounded">{alert.jurisdiction === 'all' ? 'All jurisdictions' : alert.jurisdiction}</span>
                {alert.last_checked && <span className="text-slate-600">Last checked: {new Date(alert.last_checked).toLocaleDateString()}</span>}
              </div>

              {/* Show match results inline */}
              {matchResults?.alertId === alert.id && (
                <div className="mt-3 pt-3 border-t border-slate-700/30">
                  <p className="text-xs text-slate-400 mb-2">{matchResults.matches_found} matches found ({matchResults.new_matches} new)</p>
                  <div className="space-y-2">
                    {matchResults.results?.map((r, i) => {
                      const mKey = `match-${i}`;
                      const isExp = expandedMatchId === mKey;
                      return (
                        <button key={i} onClick={() => setExpandedMatchId(isExp ? null : mKey)}
                          className="w-full text-left bg-slate-800/80 rounded p-3 hover:bg-slate-700/60 transition-colors cursor-pointer">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs px-2 py-0.5 rounded bg-slate-700/80 text-slate-300 capitalize">{r.category?.replace(/_/g, ' ')}</span>
                            <span className="text-xs text-slate-500">{Math.round(r.score * 100)}% match</span>
                          </div>
                          <p className={`text-xs text-slate-400 ${isExp ? '' : 'line-clamp-2'}`}>{r.preview}</p>
                          {isExp && r.full_text && (
                            <pre className="mt-2 pt-2 border-t border-slate-700 text-xs text-slate-300 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">{r.full_text}</pre>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        open={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        onConfirm={() => handleDelete(deleteModal?.id)}
        title="Delete Alert"
        message={`Delete alert "${deleteModal?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
      />
    </div>
  );
}

// --- Multi-Jurisdiction Comparison Tab ---

function CompareTab() {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedClauseId, setExpandedClauseId] = useState(null);

  const suggestions = [
    'limitation of liability consequential damages',
    'force majeure pandemic government action',
    'non-compete executive employment',
    'indemnification hold harmless',
    'SIAC arbitration governing law Singapore',
    'confidentiality trade secrets',
  ];

  const handleCompare = async (q) => {
    const searchQuery = q || query;
    if (!searchQuery.trim()) return;
    setQuery(searchQuery);
    setLoading(true);
    setError('');
    try {
      const data = await api.jurisdictionCompare(searchQuery);
      setResult(data);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const jurisdictionConfig = {
    us: { color: 'border-blue-800/40', headerBg: 'bg-blue-900/30', headerText: 'text-blue-300', flag: 'US' },
    singapore: { color: 'border-emerald-800/40', headerBg: 'bg-emerald-900/30', headerText: 'text-emerald-300', flag: 'SG' },
    china: { color: 'border-red-800/40', headerBg: 'bg-red-900/30', headerText: 'text-red-300', flag: 'CN' },
    crossborder: { color: 'border-purple-800/40', headerBg: 'bg-purple-900/30', headerText: 'text-purple-300', flag: 'XB' },
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <GlobeIcon className="w-5 h-5 text-slate-400" /> Multi-Jurisdiction Comparison
        </h2>
        <p className="text-slate-400 text-sm mt-1">Compare how the same clause type is drafted across US, Singapore, and China jurisdictions</p>
      </div>

      <form onSubmit={e => { e.preventDefault(); handleCompare(); }} className="mb-6">
        <div className="flex gap-3">
          <input value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Enter a clause type or keywords to compare across jurisdictions..."
            className="flex-1 px-4 py-2.5 bg-slate-800/80 border border-slate-700/60 rounded-md text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-600/40" />
          <button type="submit" disabled={loading || !query.trim()}
            className="px-6 py-2.5 bg-amber-600 hover:bg-amber-500 disabled:bg-amber-800 text-white font-medium rounded-md transition-all text-sm shrink-0">
            {loading ? 'Comparing...' : 'Compare'}
          </button>
        </div>
      </form>

      {!result && !loading && (
        <div className="mb-6">
          <p className="text-xs text-slate-500 mb-2">Try comparing:</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map(s => (
              <button key={s} onClick={() => handleCompare(s)}
                className="text-xs px-3 py-1.5 bg-slate-800/40 border border-slate-700/40 rounded-md text-slate-400 hover:text-white hover:border-slate-600/60 transition-all">
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg mb-4">{error}</div>}
      {loading && <Skeleton lines={4} />}

      {result && !loading && (
        <div className="space-y-4">
          {/* Header */}
          <div className="bg-slate-800/40 border border-slate-700/30 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Detected category:</span>
              <span className="text-xs px-2 py-0.5 rounded bg-amber-900/30 text-amber-400 capitalize">{result.detected_category?.replace(/_/g, ' ')}</span>
            </div>
            {result.remaining_searches >= 0 && (
              <span className="text-xs text-slate-600">{result.remaining_searches} searches remaining</span>
            )}
          </div>

          {/* 4-column comparison grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {Object.entries(result.columns || {}).map(([jur, col]) => {
              const config = jurisdictionConfig[jur] || { color: 'border-slate-700/40', headerBg: 'bg-slate-800', headerText: 'text-slate-300', flag: jur.toUpperCase() };
              return (
                <div key={jur} className={`border rounded-lg overflow-hidden ${config.color}`}>
                  {/* Column header */}
                  <div className={`${config.headerBg} px-4 py-3 flex items-center justify-between`}>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${config.headerBg} ${config.headerText} border ${config.color}`}>{config.flag}</span>
                      <span className={`text-sm font-medium ${config.headerText}`}>{col.label}</span>
                    </div>
                    <span className="text-xs text-slate-500">{col.count} results</span>
                  </div>

                  {/* Clauses */}
                  <div className="p-3 space-y-2">
                    {col.clauses?.length === 0 && (
                      <p className="text-xs text-slate-600 italic py-4 text-center">No matching clauses in this jurisdiction</p>
                    )}
                    {col.clauses?.map((clause, i) => {
                      const cKey = clause.clause_id || `${col.jurisdiction}-${i}`;
                      const isExpanded = expandedClauseId === cKey;
                      return (
                        <button key={cKey} onClick={() => setExpandedClauseId(isExpanded ? null : cKey)}
                          className="w-full text-left bg-slate-800/60 rounded p-3 hover:bg-slate-700/40 transition-colors cursor-pointer">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-xs px-2 py-0.5 rounded bg-slate-700/60 text-slate-400 capitalize">{clause.category?.replace(/_/g, ' ')}</span>
                            <span className="text-xs text-slate-500 tabular-nums">{Math.round(clause.score * 100)}%</span>
                          </div>
                          <p className={`text-xs text-slate-400 leading-relaxed ${isExpanded ? '' : 'line-clamp-4'}`}>{clause.preview}</p>
                          {isExpanded && clause.full_text && (
                            <pre className="mt-2 pt-2 border-t border-slate-700 text-xs text-slate-300 whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto">{clause.full_text}</pre>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-slate-500">
            Comparison across {Object.values(result.columns).reduce((sum, c) => sum + c.count, 0)} clauses from SEC EDGAR filings.
            Jurisdiction tagging based on content analysis. Not legal advice.
          </p>
        </div>
      )}
    </div>
  );
}


function DashboardTab({ user, onUpgrade, onRerunSearch }) {
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    api.getSearchHistory(30).then(setHistory).catch(() => {}).finally(() => setLoadingHistory(false));
  }, []);

  const memberSince = user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'N/A';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <LayoutDashboardIcon className="w-5 h-5 text-slate-400" /> Dashboard
        </h2>
        <p className="text-slate-400 text-sm mt-1">Your account overview and activity</p>
      </div>

      {/* Upgrade banner for free users */}
      {user.tier === 'free' && (
        <div className="bg-gradient-to-r from-amber-900/20 to-amber-800/5 border border-amber-800/20 rounded-lg p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-white mb-1">Upgrade to Pro</p>
            <p className="text-xs text-slate-400">Unlimited searches, clause benchmarking, and encrypted vault for 1,200 files.</p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={onUpgrade('annual')}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium rounded-md transition-all active:scale-[0.97]">
              S$90/mo
            </button>
            <button onClick={onUpgrade('monthly')}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm font-medium rounded-md transition-colors">
              S$180 / 6 mo
            </button>
          </div>
        </div>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-slate-800/60 border border-slate-700/40 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <BarChartIcon className="w-4 h-4 text-slate-500" />
            <p className="text-xs text-slate-500 uppercase tracking-wide">Searches This Month</p>
          </div>
          <p className="text-2xl font-bold text-white tabular-nums">{user.searches_this_month || 0}</p>
          {user.tier === 'free' && <p className="text-xs text-slate-500 mt-1">of 24 free</p>}
          {user.tier === 'pro' && <p className="text-xs text-amber-500 mt-1">Unlimited</p>}
        </div>
        <div className="bg-slate-800/60 border border-slate-700/40 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <HistoryIcon className="w-4 h-4 text-slate-500" />
            <p className="text-xs text-slate-500 uppercase tracking-wide">Total Searches</p>
          </div>
          <p className="text-2xl font-bold text-white tabular-nums">{user.total_searches || 0}</p>
          <p className="text-xs text-slate-500 mt-1">all time</p>
        </div>
        <div className="bg-slate-800/60 border border-slate-700/40 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <FileTextIcon className="w-4 h-4 text-slate-500" />
            <p className="text-xs text-slate-500 uppercase tracking-wide">Files Stored</p>
          </div>
          <p className="text-2xl font-bold text-white tabular-nums">{user.file_count || 0}</p>
          {user.tier === 'pro' && <p className="text-xs text-slate-500 mt-1">of 1,200 limit</p>}
          {user.tier === 'free' && <p className="text-xs text-slate-500 mt-1">Pro only</p>}
        </div>
        <div className="bg-slate-800/60 border border-slate-700/40 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <UserIcon className="w-4 h-4 text-slate-500" />
            <p className="text-xs text-slate-500 uppercase tracking-wide">Plan</p>
          </div>
          <p className="text-2xl font-bold text-white capitalize">{user.tier}</p>
          {user.tier === 'free' ? (
            <button onClick={onUpgrade('annual')} className="text-xs text-amber-400 hover:text-amber-300 mt-1 transition-colors">Upgrade &rarr;</button>
          ) : (
            <p className="text-xs text-slate-500 mt-1">Since {memberSince}</p>
          )}
        </div>
      </div>

      {/* Account section */}
      <div className="bg-slate-800/60 border border-slate-700/40 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
          <CreditCardIcon className="w-4 h-4 text-slate-500" /> Account & Billing
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-slate-700">
            <span className="text-sm text-slate-400">Email</span>
            <span className="text-sm text-white">{user.email}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-slate-700">
            <span className="text-sm text-slate-400">Plan</span>
            <span className={`text-sm font-medium ${user.tier === 'pro' ? 'text-amber-400' : 'text-slate-300'}`}>
              {user.tier === 'pro' ? 'Pro' : 'Free'}
            </span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-slate-400">Billing</span>
            {user.stripe_customer_id ? (
              <button onClick={async () => {
                try {
                  const { url } = await api.openBillingPortal();
                  window.location.href = url;
                } catch (err) { alert(err.message); }
              }} className="text-sm text-amber-400 hover:text-amber-300 transition-colors">
                Manage subscription &rarr;
              </button>
            ) : user.tier === 'free' ? (
              <button onClick={onUpgrade('annual')} className="text-sm text-amber-400 hover:text-amber-300 transition-colors">
                Upgrade to Pro &rarr;
              </button>
            ) : (
              <span className="text-sm text-slate-500">No active subscription</span>
            )}
          </div>
        </div>
      </div>

      {/* Test Report Purchase — owner only */}
      {user.email === 'adrian.siew@era-investment.com' && (
        <div className="bg-slate-800/40 border border-slate-700/30 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-white mb-2">Test Payment</h3>
          <p className="text-xs text-slate-400 mb-4">
            Buy a S$10 Contract Intelligence Report to test the full Stripe cycle: payment, refund, and payout to DBS.
          </p>
          <button
            onClick={async () => {
              try {
                const { url } = await api.createCheckoutReport();
                window.location.href = url;
              } catch (err) { alert(err.message); }
            }}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium rounded-md transition-all active:scale-[0.97]"
          >
            Buy Report — S$10
          </button>
        </div>
      )}

      {/* Search history */}
      <div className="bg-slate-800/60 border border-slate-700/40 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
          <HistoryIcon className="w-4 h-4 text-amber-500" /> Recent Searches
        </h3>
        <p className="text-xs text-slate-500 mb-4">Click any entry to re-run, review or refine your search criteria.</p>
        {loadingHistory ? <Skeleton lines={3} /> : history.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center">No search history yet. Start searching to see your activity here.</p>
        ) : (
          <div className="space-y-1">
            {history.map((h, i) => {
              const isBenchmark = h.query.startsWith('[benchmark]');
              const displayQuery = isBenchmark ? h.query.replace('[benchmark] ', '') : h.query;
              return (
                <button key={i} onClick={() => onRerunSearch(displayQuery)}
                  className="w-full flex items-center justify-between py-2.5 border-b border-slate-700/50 last:border-0 hover:bg-slate-700/30 rounded px-2 -mx-2 transition-colors text-left group cursor-pointer">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-300 truncate group-hover:text-white transition-colors">
                      {isBenchmark ? (
                        <><span className="text-xs px-1.5 py-0.5 rounded bg-slate-700 text-amber-400 mr-2">Benchmark</span>{displayQuery}</>
                      ) : displayQuery}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-4">
                    <span className="text-xs text-slate-500">{h.results_count} results</span>
                    <span className="text-xs text-slate-600">{new Date(h.searched_at).toLocaleDateString()} {new Date(h.searched_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    <ChevronRightIcon className="w-3.5 h-3.5 text-slate-600 group-hover:text-amber-400 transition-colors" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Legal Page Wrapper ---

function LegalPage({ title, children, onBack }) {
  return (
    <div className="min-h-screen bg-slate-900">
      <nav className="bg-slate-800/95 backdrop-blur-md border-b border-slate-700 px-4 sm:px-6 py-3 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <a href="/" onClick={e => { e.preventDefault(); onBack(); }}><Logo /></a>
          <a href="/" onClick={e => { e.preventDefault(); onBack(); }} className="text-sm text-slate-400 hover:text-white transition-colors">Back to Home</a>
        </div>
      </nav>
      <div className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="text-3xl font-bold text-white mb-8 font-serif">{title}</h1>
        <div className="prose prose-invert prose-sm max-w-none text-slate-300 leading-relaxed space-y-6">
          {children}
        </div>
      </div>
      <footer className="py-8 px-6 bg-slate-950 text-center">
        <p className="text-xs text-slate-700">&copy; {new Date().getFullYear()} icontracts.net. All rights reserved.</p>
      </footer>
    </div>
  );
}

// --- Terms of Service ---

function TermsPage({ onBack }) {
  return (
    <LegalPage title="Terms of Service" onBack={onBack}>
      <p className="text-xs text-slate-500">Last updated: 2 April 2026</p>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">1. Agreement to Terms</h2>
        <p>By accessing or using icontracts.net (the "Service"), operated by icontracts.net and its affiliates ("we", "us", "our"), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Service.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">2. Description of Service</h2>
        <p>icontracts.net is a legal contract intelligence platform that provides:</p>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>Clause Discovery — semantic search across publicly filed commercial contracts</li>
          <li>Clause Benchmarking — grading clauses against the corpus for pattern similarity</li>
          <li>Encrypted Vault (Pro) — AES-256-GCM encrypted file storage</li>
        </ul>
        <p className="mt-3">The Service is a research and reference tool. It does not provide legal advice and should not be relied upon as a substitute for professional legal counsel.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">3. Accounts</h2>
        <p>You must provide a valid email address and create a password to register. You are responsible for maintaining the confidentiality of your account credentials. You must notify us immediately of any unauthorized use of your account.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">4. Free and Pro Plans</h2>
        <p>Free accounts are limited to {24} searches per month. Pro accounts require a paid subscription and include unlimited searches and access to the Encrypted Vault. Pricing is displayed in Singapore Dollars (SGD) on our pricing page.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">5. Payments and Refunds</h2>
        <p>Payments are processed by Stripe. By subscribing to a Pro plan, you authorize recurring charges as described at checkout. You may cancel your subscription at any time through the Stripe customer portal, which is accessible from your Dashboard. We offer a 14-day no-questions-asked refund: within 14 days of your first Pro payment, email us and we will issue a full refund and downgrade your account to Free. After 14 days, refunds are handled on a case-by-case basis.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">6. Encrypted Vault</h2>
        <p>Files uploaded to the Encrypted Vault are encrypted using AES-256-GCM with a key derived from your chosen encryption password. We do not store your encryption password. If you forget your encryption password, your files cannot be recovered. This is by design.</p>
        <p className="mt-3">The Vault operates on a zero-knowledge basis. Files are encrypted prior to storage on our servers and can only be decrypted with your password. We do not have access to your encryption password and cannot read the contents of your files. This property is enforced by the system's architecture, not by policy.</p>
        <p className="mt-3">By uploading files to the Vault, you warrant that you have the legal right to do so. You are solely responsible for compliance with applicable data protection laws (including Singapore's Personal Data Protection Act, the EU General Data Protection Regulation where applicable, and any professional confidentiality obligations you owe your own clients). You agree to indemnify us against any third-party claims arising from content you upload.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">7. Data Sources</h2>
        <p>The clause database is sourced from publicly filed commercial contracts and regulatory disclosures, including SEC EDGAR filings, SGX announcements, HKEX filings, SSE and SZSE filings. All source data is publicly available. Search results return clause excerpts with citations back to the original filing for verification.</p>
        <p className="mt-3">We do not redistribute full filings. We provide transformative analysis, search, and benchmarking services over publicly available disclosures. We do not guarantee the accuracy, completeness, or currency of any clause or search result. If you are the author or rightful owner of a specific clause and wish it removed from the index, email us and we will remove it within seven (7) business days.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">8. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>Use the Service for any unlawful purpose</li>
          <li>Attempt to gain unauthorized access to the Service or its infrastructure</li>
          <li>Scrape, crawl, or systematically download data from the Service</li>
          <li>Reverse engineer or decompile any part of the Service</li>
          <li>Resell or redistribute search results without authorization</li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">9. Disclaimer of Warranties</h2>
        <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE. SEARCH RESULTS REFLECT PATTERN SIMILARITY, NOT LEGAL QUALITY OR APPLICABILITY.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">10. Limitation of Liability</h2>
        <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY YOU IN THE TWELVE MONTHS PRECEDING THE CLAIM.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">11. Governing Law</h2>
        <p>These Terms are governed by the laws of the Republic of Singapore. Any disputes arising from or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of Singapore.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">12. Changes to Terms</h2>
        <p>We reserve the right to modify these Terms at any time. Material changes will be communicated via the email address associated with your account. Continued use of the Service after changes constitutes acceptance of the modified Terms.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">13. Contact</h2>
        <p>For questions about these Terms, contact us at <a href="mailto:support@icontracts.net" className="text-amber-500 hover:text-amber-400">support@icontracts.net</a>.</p>
      </section>
    </LegalPage>
  );
}

// --- Privacy Policy ---

function PrivacyPage({ onBack }) {
  return (
    <LegalPage title="Privacy Policy" onBack={onBack}>
      <p className="text-xs text-slate-500">Last updated: 2 April 2026</p>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">1. Introduction</h2>
        <p>icontracts.net and its affiliates ("we", "us", "our") operates icontracts.net. This Privacy Policy explains how we collect, use, and protect your personal data in compliance with the Singapore Personal Data Protection Act 2012 (PDPA).</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">2. Data We Collect</h2>
        <p><strong className="text-white">Account Data:</strong> Email address and hashed password (PBKDF2). We do not store your password in plain text.</p>
        <p className="mt-2"><strong className="text-white">Usage Data:</strong> Search queries, search timestamps, and result counts for the purpose of providing search history and usage statistics.</p>
        <p className="mt-2"><strong className="text-white">Payment Data:</strong> Processed and stored by Stripe. We store only your Stripe customer ID. We do not store credit card numbers, bank account details, or other payment credentials.</p>
        <p className="mt-2"><strong className="text-white">Encrypted Vault Files:</strong> Files are encrypted with AES-256-GCM before storage. We cannot read the contents of your encrypted files.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">3. How We Use Your Data</h2>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>To provide and maintain the Service</li>
          <li>To process payments and manage subscriptions</li>
          <li>To display your search history and usage statistics</li>
          <li>To communicate important service updates</li>
        </ul>
        <p className="mt-3">We do <strong className="text-white">not</strong> sell, rent, or share your personal data with third parties for marketing purposes. We do <strong className="text-white">not</strong> use your search queries or uploaded files to train machine learning models.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">4. Data Storage and Security</h2>
        <p>Your data is stored on infrastructure located in Singapore. We implement industry-standard security measures including:</p>
        <ul className="list-disc list-inside space-y-1 ml-2 mt-2">
          <li>PBKDF2 password hashing with unique salts</li>
          <li>AES-256-GCM encryption for vault files</li>
          <li>HTTPS/TLS encryption for all data in transit</li>
          <li>SQLite WAL mode for database integrity</li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">5. Third-Party Services</h2>
        <p>We use the following third-party services:</p>
        <ul className="list-disc list-inside space-y-1 ml-2 mt-2">
          <li><strong className="text-white">Stripe</strong> — Payment processing (<a href="https://stripe.com/privacy" className="text-amber-500 hover:text-amber-400" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy</a>)</li>
          <li><strong className="text-white">Cloudflare</strong> — DNS, CDN, and tunnel services (<a href="https://www.cloudflare.com/privacypolicy/" className="text-amber-500 hover:text-amber-400" target="_blank" rel="noopener noreferrer">Cloudflare Privacy Policy</a>)</li>
          <li><strong className="text-white">Google Fonts</strong> — Font delivery (Inter, Playfair Display)</li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">6. Cookies</h2>
        <p>We use a session cookie to maintain your logged-in state. We do not use tracking cookies, analytics cookies, or third-party advertising cookies.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">7. Your Rights Under the PDPA</h2>
        <p>You have the right to:</p>
        <ul className="list-disc list-inside space-y-1 ml-2 mt-2">
          <li><strong className="text-white">Access</strong> — Request a copy of the personal data we hold about you</li>
          <li><strong className="text-white">Correction</strong> — Request correction of inaccurate personal data</li>
          <li><strong className="text-white">Withdrawal of Consent</strong> — Withdraw consent for the collection, use, or disclosure of your personal data (note: this may result in our inability to provide the Service)</li>
        </ul>
        <p className="mt-3">To exercise these rights, contact us at <a href="mailto:support@icontracts.net" className="text-amber-500 hover:text-amber-400">support@icontracts.net</a>.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">8. Data Retention</h2>
        <p>We retain your account data and search history for as long as your account is active. Upon account deletion, your personal data and encrypted vault files will be permanently deleted within 30 days. Anonymized, aggregated usage statistics may be retained indefinitely.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">9. Data Breach Notification</h2>
        <p>In the event of a data breach that is likely to result in significant harm, we will notify affected individuals and the Personal Data Protection Commission (PDPC) as required under the PDPA.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">10. Changes to This Policy</h2>
        <p>We may update this Privacy Policy from time to time. Material changes will be communicated via email. The "Last updated" date at the top reflects the most recent revision.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-white mt-8 mb-3">11. Contact / Data Protection Officer</h2>
        <p>For privacy-related inquiries:</p>
        <p className="mt-2">icontracts.net and its affiliates<br />Email: <a href="mailto:support@icontracts.net" className="text-amber-500 hover:text-amber-400">support@icontracts.net</a></p>
      </section>
    </LegalPage>
  );
}

// --- Main App ---

export default function App() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState('search');
  const [page, setPage] = useState('landing');
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [billingMessage, setBillingMessage] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [initialQuery, setInitialQuery] = useState('');

  const refreshUser = useCallback(() => {
    api.getMe().then(setUser).catch(() => {});
  }, []);

  useEffect(() => {
    // Browser back/forward button support
    const handlePopState = () => {
      const p = window.location.pathname;
      if (p === '/signin') { setPage('auth'); }
      else { setPage(prev => prev === 'auth' ? 'landing' : prev); }
    };
    window.addEventListener('popstate', handlePopState);

    // Handle /terms and /privacy routes
    const path = window.location.pathname;
    if (path === '/terms') { setPage('terms'); setCheckingAuth(false); return; }
    if (path === '/privacy') { setPage('privacy'); setCheckingAuth(false); return; }
    if (path === '/MSA2026') { setPage('msa2026'); setCheckingAuth(false); return; }
    if (path === '/signin') { setPage('auth'); setCheckingAuth(false); return; }

    const params = new URLSearchParams(window.location.search);
    if (params.get('billing') === 'success') {
      setBillingMessage('Payment successful! Your account has been upgraded to Pro.');
      window.history.replaceState({}, '', '/');
      const poll = setInterval(() => {
        api.getMe().then(u => { setUser(u); if (u.tier === 'pro') clearInterval(poll); }).catch(() => {});
      }, 2000);
      setTimeout(() => clearInterval(poll), 30000);
    }
    if (params.get('billing') === 'cancel') {
      setBillingMessage('Payment was cancelled.');
      window.history.replaceState({}, '', '/');
    }

    api.getMe()
      .then(u => { setUser(u); setPage('app'); })
      .catch(() => setPage('landing'))
      .finally(() => setCheckingAuth(false));
  }, []);

  const handleUpgrade = (plan) => async () => {
    try {
      const { url } = await api.createCheckout(plan);
      window.location.href = url;
    } catch (err) { alert(err.message); }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-pulse"><Logo size="large" /></div>
      </div>
    );
  }

  const goHome = () => { window.history.pushState({}, '', '/'); setPage(user ? 'app' : 'landing'); };

  if (page === 'terms') return <TermsPage onBack={goHome} />;
  if (page === 'privacy') return <PrivacyPage onBack={goHome} />;
  if (page === 'msa2026') return <MSA2026Page />;

  if (page === 'landing' && !user) {
    return <LandingPage onNavigateToAuth={() => { window.history.pushState({}, '', '/signin'); setPage('auth'); }} />;
  }

  if (page === 'auth' && !user) {
    return <AuthForm onAuth={(u) => { setUser(u); setPage('app'); }} onBack={() => { window.history.pushState({}, '', '/'); setPage('landing'); }} />;
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboardIcon },
    { id: 'search', label: 'Discovery', icon: SearchIcon },
    { id: 'benchmark', label: 'Benchmarking', icon: ScaleIcon },
    { id: 'library', label: 'Library', icon: BookIcon },
    { id: 'compare', label: 'Compare', icon: GlobeIcon },
    { id: 'ip', label: 'IP', icon: ShieldIcon },
    { id: 'templates', label: 'Templates', icon: FileTextIcon },
    { id: 'evolution', label: 'Evolution', icon: HistoryIcon },
    { id: 'alerts', label: 'Alerts', icon: BellIcon },
    { id: 'arbitration', label: 'Arbitration', icon: ScaleIcon },
    { id: 'vault', label: 'Vault', icon: LockIcon },
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Top nav */}
      <nav className="bg-slate-800/90 backdrop-blur-lg border-b border-slate-700/40 px-4 sm:px-6 py-3 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <button onClick={() => setTab('dashboard')}><Logo /></button>

            {/* Desktop tabs */}
            <div className="hidden md:flex gap-0.5">
              {tabs.map(t => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-1.5 ${
                    tab === t.id ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                  }`}>
                  <t.icon className="w-4 h-4" />
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Desktop user menu */}
          <div className="hidden md:flex items-center gap-4">
            <span className="text-sm text-slate-500 truncate max-w-[200px]">{user.email}</span>
            <span className={`text-xs px-2 py-0.5 rounded font-medium ${
              user.tier === 'pro' ? 'bg-amber-900/40 text-amber-400' : 'bg-slate-700/60 text-slate-400'
            }`}>
              {user.tier === 'pro' ? 'PRO' : 'FREE'}
            </span>
            <button onClick={async () => { await api.logout(); setUser(null); setPage('landing'); }}
              className="text-slate-500 hover:text-white p-1.5 hover:bg-slate-700/50 rounded-md transition-colors" title="Sign Out">
              <LogOutIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Mobile hamburger */}
          <button className="md:hidden p-2 text-slate-400 hover:text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <XIcon className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-3 pt-3 border-t border-slate-700/40 space-y-1">
            {tabs.map(t => (
              <button key={t.id} onClick={() => { setTab(t.id); setMobileMenuOpen(false); }}
                className={`w-full px-3 py-2.5 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${
                  tab === t.id ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`}>
                <t.icon className="w-4 h-4" />
                {t.label}
              </button>
            ))}
            <div className="pt-3 mt-2 border-t border-slate-700/40 flex items-center justify-between px-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-400">{user.email}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${user.tier === 'pro' ? 'bg-amber-900/40 text-amber-400' : 'bg-slate-700/60 text-slate-400'}`}>
                  {user.tier}
                </span>
              </div>
              <button onClick={async () => { await api.logout(); setUser(null); setPage('landing'); }}
                className="text-sm text-slate-400 hover:text-white">Sign Out</button>
            </div>
          </div>
        )}
      </nav>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {billingMessage && (
          <div className={`mb-6 px-4 py-3 rounded-lg border flex items-center justify-between ${
            billingMessage.includes('successful') ? 'bg-green-900/40 border-green-800/50 text-green-200' : 'bg-yellow-900/40 border-yellow-800/50 text-yellow-200'
          }`}>
            <span>{billingMessage}</span>
            <button onClick={() => setBillingMessage('')} className="text-xs opacity-60 hover:opacity-100 ml-4">dismiss</button>
          </div>
        )}
        {tab === 'dashboard' && <DashboardTab user={user} onUpgrade={handleUpgrade} onRerunSearch={(query) => { setInitialQuery(query); setTab('search'); }} />}
        {tab === 'search' && <SearchTab initialQuery={initialQuery} onConsumeQuery={() => setInitialQuery('')} />}
        {tab === 'benchmark' && <BenchmarkTab />}
        {tab === 'library' && <LibraryTab />}
        {tab === 'compare' && <CompareTab />}
        {tab === 'ip' && <IPTab user={user} />}
        {tab === 'templates' && <TemplatesTab />}
        {tab === 'evolution' && <EvolutionTab />}
        {tab === 'alerts' && <AlertsTab />}
        {tab === 'arbitration' && <ArbitrationTab />}
        {tab === 'vault' && <VaultTab user={user} onUpgrade={handleUpgrade} />}
      </main>
    </div>
  );
}
